<template>
  <div id="ProductBundles">
    <v-card max-width="100%" outlined>
      <v-row no-gutters class="align-center">
        <v-card-title class="text-h4"> Product Bundles </v-card-title>
        <v-spacer />
        <div
          class="d-flex flex-row px-4 flex-wrap align-center"
          style="gap: 24px"
        >
          <v-btn
            v-if="userGroups?.includes('superadmin')"
            color="secondary"
            class="black--text"
            @click="createProductBundleSEOs"
            :disabled="offline || loading"
          >
            Create ProductBundle SEOs
          </v-btn>

          <v-btn
            color="primary"
            class=""
            @click="createItem"
            :disabled="offline || savingSelectedProductBundles"
          >
            New Bundle
          </v-btn>
        </div>
      </v-row>

      <v-row class="px-4">
        <v-col cols="12" sm="4" md="3">
          <v-text-field
            v-model="productBundlesDataTableSearch"
            append-icon="mdi-magnify"
            label="Search"
            @input="changeProductBundlesSearchQuery"
          >
          </v-text-field>
        </v-col>
        <v-col cols="6" sm="4" md="3">
          <v-combobox
            class="productBundlesSearchInputCSS pt-2"
            v-model="productBundlesSearchTags"
            label="Tags"
            multiple
            chips
            dense
            @change="changeProductBundlesSearchQuery"
            min-height="36px"
            deletable-chips
            clearable
            :items="syncedProductTagArray"
            item-text="name"
            :return-object="false"
            item-value="id"
          ></v-combobox>
        </v-col>
        <v-col cols="6" sm="4" md="3">
          <v-menu
            v-model="searchProductBundleCategoriesMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            :nudge-bottom="-12"
            :rounded="false"
          >
            <template v-slot:activator="{ on }">
              <div @click="toggleMenu">
                <v-combobox
                  class="productBundlesSearchInputCSS pt-2"
                  :value="computedProductBundlesSearchCategories"
                  label="Categories"
                  multiple
                  chips
                  dense
                  min-height="36px"
                  clearable
                  v-on="on"
                  readonly
                  @click:clear="productBundlesDataTableSearchCategories = []"
                ></v-combobox>
              </div>
            </template>
            <v-card>
              <v-card-text>
                <v-treeview
                  :items="categoryTree"
                  color="primary"
                  selected-color="primary"
                  selectable
                  item-key="id"
                  open-on-click
                  selection-type="independent"
                  v-model="productBundlesDataTableSearchCategories"
                  @input="changeProductBundlesSearchQuery"
                ></v-treeview>
              </v-card-text>
            </v-card>
          </v-menu>
        </v-col>

        <v-col cols="6" sm="4" md="3">
          <v-autocomplete
            class="productBundlesSearchInputCSS pt-2"
            v-model="productBundlesDataTableSearchSubjects"
            label="Subjects"
            :items="subjectFilterItems"
            :item-text="subjectLabel"
            item-value="id"
            :filter="subjectMatchesSearch"
            :loading="loadingSubjects"
            multiple
            chips
            dense
            min-height="36px"
            deletable-chips
            clearable
            @change="changeProductBundlesSearchQuery"
          ></v-autocomplete>
        </v-col>

        <v-col cols="4" sm="3" md="2">
          <v-checkbox
            v-model="bulkEditAll"
            label="Bulk Edit All"
            class="mt-0"
          ></v-checkbox>
        </v-col>
        <v-col cols="4" sm="3" md="2">
          <v-checkbox
            v-model="showOnlySoftDeleted"
            label="Show Only Soft Deleted"
            @change="changeProductBundlesSearchQuery"
            dense
          ></v-checkbox>
        </v-col>
        <v-col cols="4" sm="3" md="2">
          <v-checkbox
            v-model="showPriceMismatches"
            label="Show Price Mismatches"
            @change="changeProductBundlesSearchQuery"
            dense
          ></v-checkbox>
        </v-col>
      </v-row>

      <div v-if="selectedProductBundles.length > 0 || bulkEditAll">
        <v-card-title class="text-h5 pb-4 pt-0"> Bulk Edit </v-card-title>

        <div class="px-3 pb-3" style="max-width: 470px">
          <!-- <v-col class="pt-0" cols="12" sm="6" md="4"> -->
          <v-expansion-panels
            class="mb-3"
            style="justify-content: left"
            v-model="expBulkEditPanels"
          >
            <v-expansion-panel>
              <v-expansion-panel-header class="pa-2">
                <div class="d-flex align-center">
                  <v-icon class="mr-1"> mdi-star </v-icon>
                  <div>
                    <span> Feature</span>
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content
                id="expPanelContent"
                color="grey lighten-3"
              >
                <v-row class="pa-3 d-flex" style="gap: 12px">
                  <v-btn
                    color="primary"
                    @click="featureProductBundles"
                    :disabled="
                      offline ||
                      savingSelectedProductBundles ||
                      (bulkEditLoading !== null &&
                        bulkEditLoading !== 'feature')
                    "
                    :loading="bulkEditLoading === 'feature'"
                  >
                    <v-icon left> mdi-star </v-icon>Feature Product Bundles
                  </v-btn>
                  <v-btn
                    color="primary"
                    @click="unFeatureProductBundles"
                    :disabled="
                      offline ||
                      savingSelectedProductBundles ||
                      (bulkEditLoading !== null &&
                        bulkEditLoading !== 'unfeature')
                    "
                    :loading="bulkEditLoading === 'unfeature'"
                  >
                    <v-icon left> mdi-star-outline </v-icon>Unfeature Product
                    Bundles
                  </v-btn>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header class="pa-2">
                <div class="d-flex align-center">
                  <v-icon class="mr-1">mdi-calendar</v-icon>
                  <div>
                    <span> Schedule</span>
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content
                id="expPanelContent"
                color="grey lighten-3"
              >
                <v-row class="pa-3 d-flex align-center" style="gap: 12px">
                  <v-dialog
                    v-model="bulkEditDateTimeDialog"
                    persistent
                    width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formattedBulkEditPublishDate"
                        readonly
                        label="Publish Date"
                        v-bind="{ ...attrs, ...$attrs }"
                        v-on="on"
                        clearable
                        @click:clear="updateBulkEditPublishDateTime('clear')"
                      ></v-text-field>
                    </template>
                    <v-card class="t-datetime-picker white">
                      <v-toolbar height="36" color="primary" dark flat>
                        <v-tabs v-model="bulkEditDateTimeTab" grow height="36">
                          <v-tabs-slider color="white"></v-tabs-slider>
                          <v-tab href="#date">
                            <v-icon>mdi-calendar</v-icon>
                          </v-tab>
                          <v-tab href="#time">
                            <v-icon>mdi-clock</v-icon>
                          </v-tab>
                          <v-btn
                            depressed
                            text
                            class="rounded-0"
                            @click="closeBulkEditDateTimeDialog"
                          >
                            <v-icon>mdi-check</v-icon>
                          </v-btn>
                        </v-tabs>
                      </v-toolbar>

                      <v-tabs-items v-model="bulkEditDateTimeTab">
                        <v-tab-item value="date">
                          <v-date-picker
                            v-model="bulkEditPublishDate"
                            @change="updateBulkEditPublishDateTime"
                            class="rounded-0"
                            @input="bulkEditDateTimeTab = 'time'"
                            full-width
                          >
                          </v-date-picker>
                        </v-tab-item>
                        <v-tab-item value="time">
                          <v-time-picker
                            :key="bulkEditDateTimeTab"
                            v-model="bulkEditPublishTime"
                            format="24hr"
                            class="rounded-0"
                            full-width
                            @change="updateBulkEditPublishDateTime"
                          >
                          </v-time-picker>
                        </v-tab-item>
                      </v-tabs-items>
                    </v-card>
                  </v-dialog>
                  <v-btn
                    color="primary"
                    @click="scheduleProductBundles"
                    :disabled="
                      offline ||
                      savingSelectedProductBundles ||
                      !bulkEditPublishDateTime ||
                      (bulkEditLoading !== null &&
                        bulkEditLoading !== 'schedule')
                    "
                    :loading="bulkEditLoading === 'schedule'"
                  >
                    Schedule
                  </v-btn>
                  <v-btn
                    color="primary"
                    @click="unScheduleProductBundles"
                    :disabled="
                      offline ||
                      savingSelectedProductBundles ||
                      (bulkEditLoading !== null &&
                        bulkEditLoading !== 'unschedule')
                    "
                    :loading="bulkEditLoading === 'unschedule'"
                  >
                    Un-Publish
                  </v-btn>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header class="pa-2">
                <div class="d-flex align-center">
                  <v-icon class="mr-1">mdi-currency-usd</v-icon>
                  <div>
                    <span> Change Price</span>
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content
                id="expPanelContent"
                color="grey lighten-3"
              >
                <v-row
                  class="pa-3 d-flex align-center justify-end"
                  style="gap: 12px"
                >
                  <v-text-field
                    v-model="bulkEditPriceChangeValue"
                    label="Price Change Value"
                    type="number"
                    prepend-icon="mdi-alpha-r"
                    @input="bulkEditPriceChangePercent = null"
                  ></v-text-field>

                  <v-text-field
                    v-model="bulkEditPriceChangePercent"
                    label="Price Change Percentage"
                    type="number"
                    append-icon="mdi-percent"
                    @input="bulkEditPriceChangeValue = null"
                  ></v-text-field>
                  <!--v-btn
                    color="primary"
                    @click="resetProductBundlePrices"
                    :disabled="
                      offline ||
                      savingSelectedProductBundles ||
                      !showPriceMismatches ||
                      !hasMismatchedProductBundles ||
                      (bulkEditLoading !== null &&
                        bulkEditLoading !== 'reset_price')
                    "
                    :loading="bulkEditLoading === 'reset_price'"
                  >
                    Reset
                  </!--v-btn-->
                  <v-btn
                    color="primary"
                    @click="changeProductBundlesPrice('increase')"
                    :disabled="
                      offline ||
                      savingSelectedProductBundles ||
                      ((!bulkEditPriceChangePercent ||
                        bulkEditPriceChangePercent === '') &&
                        (!bulkEditPriceChangeValue ||
                          bulkEditPriceChangeValue === '')) ||
                      (bulkEditLoading !== null &&
                        bulkEditLoading !== 'increase_price')
                    "
                    :loading="bulkEditLoading === 'increase_price'"
                  >
                    Increase
                  </v-btn>
                  <v-btn
                    color="primary"
                    @click="changeProductBundlesPrice('decrease')"
                    :disabled="
                      offline ||
                      savingSelectedProductBundles ||
                      ((!bulkEditPriceChangePercent ||
                        bulkEditPriceChangePercent === '') &&
                        (!bulkEditPriceChangeValue ||
                          bulkEditPriceChangeValue === '')) ||
                      (bulkEditLoading !== null &&
                        bulkEditLoading !== 'decrease_price')
                    "
                    :loading="bulkEditLoading === 'decrease_price'"
                  >
                    Decrease
                  </v-btn>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header class="pa-2">
                <div class="d-flex align-center">
                  <v-icon class="mr-1">mdi-sale</v-icon>
                  <div>
                    <span>Discount</span>
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content
                id="expPanelContent"
                color="grey lighten-3"
              >
                <v-row
                  class="pa-3 d-flex align-center justify-end"
                  style="gap: 12px"
                >
                  <v-text-field
                    v-model="bulkEditDiscountPercent"
                    label="Discount Percentage"
                    type="number"
                    append-icon="mdi-percent"
                  ></v-text-field>
                  <v-btn
                    color="primary"
                    @click="discountProductBundles"
                    :disabled="
                      offline ||
                      savingSelectedProductBundles ||
                      !bulkEditDiscountPercent ||
                      bulkEditDiscountPercent === '' ||
                      (bulkEditLoading !== null &&
                        bulkEditLoading !== 'discount')
                    "
                    :loading="bulkEditLoading === 'discount'"
                  >
                    Discount
                  </v-btn>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </div>

      <div class="px-4">
        <v-data-table
          :footer-props="{
            showFirstLastPage: true,
          }"
          id="productBundlesDataTable"
          :headers="headers"
          :items="productBundles"
          :sort-desc.sync="sortDesc"
          :sort-by.sync="sortBy"
          :items-per-page.sync="productBundlesDataTableRowsPerPage"
          @update:items-per-page="queryProductBundles"
          :page.sync="productBundlesDataTablePageCount"
          @update:page="productBundlesDataTablePageCountUpdate"
          :server-items-length="productBundlesDataTableServerItemsLength"
          must-sort
          :loading="!syncedProductBundles && productBundles?.length === 0"
          loading-text="Loading Product Bundles..."
          no-data-text="No Product Bundles found"
          :show-select="!bulkEditAll"
          v-model="selectedProductBundles"
        >
          <template v-slot:[`item.featured`]="{ item }">
            <v-icon v-if="item.featured">mdi-star</v-icon>
            <v-icon v-else>mdi-star-outline</v-icon>
          </template>
          <template v-slot:[`item.price`]="{ item }">
            {{ formatPrice(item.price) }}
          </template>
          <template v-slot:[`item.discountPercentage`]="{ item }">
            {{ formatPercentage(item.discountPercentage) }}
          </template>
          <template v-slot:[`item.categoryId`]="{ item }">
            {{ formatCategoryId(item.categoryId) }}
          </template>
          <template v-slot:[`item.subjectID`]="{ item }">
            {{
              item.subjectID
                ? subjectLabel(subjectsByID[item.subjectID])
                : "—"
            }}
          </template>
          <template v-slot:[`item.publishDate`]="{ item }">
            {{ formatDateTime(item.publishDate) }}
          </template>
          <template v-slot:[`item.updatedAt`]="{ item }">
            {{ formatDateTime(item.updatedAt) }}
          </template>

          <template v-slot:[`item.softDeleted`]="{ item }">
            <v-chip
              v-if="item.softDeleted"
              small
              color="error"
              text-color="white"
            >
              <v-icon x-small left>mdi-trash-can</v-icon>
              Deleted
            </v-chip>
            <span v-else class="text--secondary">—</span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-icon small class="mr-2" @click="shareProduct(item)">
              mdi-share
            </v-icon>
            <v-icon
              small
              class="mr-2"
              :disabled="offline || loading || savingSelectedProductBundles"
              @click="duplicateItem(item)"
            >
              mdi-content-copy
            </v-icon>
            <v-icon
              small
              class="mr-2"
              @click="editItem(item)"
              :disabled="offline || loading || savingSelectedProductBundles"
            >
              mdi-pencil
            </v-icon>
            <v-icon small class="mr-2" @click="copyProductID(item)">
              mdi-identifier
            </v-icon>
            <v-icon
              small
              class="ml-2"
              color="error"
              :disabled="offline || loading || savingSelectedProductBundles"
              @click="toggleSoftDeleteProductBundle(item)"
              :title="
                item.softDeleted ? 'Restore Bundle' : 'Soft Delete Bundle'
              "
            >
              {{ item.softDeleted ? "mdi-delete-restore" : "mdi-trash-can" }}
            </v-icon>
          </template>
        </v-data-table>
      </div>
    </v-card>

    <v-dialog persistent v-if="dialog" v-model="dialog" max-width="800px">
      <v-card>
        <v-form ref="form" v-model="valid" @submit.prevent="save">
          <v-card-title>
            <span class="text-h5">{{ formTitle }}</span>
          </v-card-title>

          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedItem.title"
                    label="Title"
                    :rules="formRules.title"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-checkbox
                    v-model="editedItem.featured"
                    label="Featured"
                  ></v-checkbox
                ></v-col>
                <v-col cols="6" sm="3">
                  <v-text-field
                    v-model="editedItem.price"
                    label="Price"
                    type="number"
                    prefix="R"
                    :hint="`${
                      totalProductsPrice === 'No products selected'
                        ? ''
                        : 'Total products price: R ' + totalProductsPrice
                    }`"
                    persistent-hint
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field
                    v-model="editedItem.discountPercentage"
                    label="Discount"
                    type="number"
                    suffix="%"
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field
                    :value="productBundleVat"
                    label="Vat (15%)"
                    type="number"
                    prefix="R"
                    readonly
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-dialog v-model="datetimeDialog" persistent width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formattedProductBundlePublishDate"
                        readonly
                        label="Publish Date"
                        v-bind="{ ...attrs, ...$attrs }"
                        v-on="on"
                        clearable
                        @click:clear="
                          updateProductBundlePublishDateTime('clear')
                        "
                      ></v-text-field>
                    </template>
                    <v-card class="t-datetime-picker white">
                      <v-toolbar height="36" color="primary" dark flat>
                        <v-tabs v-model="dateTimeTab" grow height="36">
                          <v-tabs-slider color="white"></v-tabs-slider>
                          <v-tab href="#date">
                            <v-icon>mdi-calendar</v-icon>
                          </v-tab>
                          <v-tab href="#time">
                            <v-icon>mdi-clock</v-icon>
                          </v-tab>
                          <v-btn
                            depressed
                            text
                            class="rounded-0"
                            @click="closeDateTimeDialog"
                          >
                            <v-icon>mdi-check</v-icon>
                          </v-btn>
                        </v-tabs>
                      </v-toolbar>

                      <v-tabs-items v-model="dateTimeTab">
                        <v-tab-item value="date">
                          <v-date-picker
                            v-model="productBundlePublishDate"
                            @change="updateProductBundlePublishDateTime"
                            class="rounded-0"
                            @input="dateTimeTab = 'time'"
                            full-width
                          >
                          </v-date-picker>
                        </v-tab-item>
                        <v-tab-item value="time">
                          <v-time-picker
                            :key="dateTimeTab"
                            v-model="productBundlePublishTime"
                            format="24hr"
                            class="rounded-0"
                            full-width
                            @change="updateProductBundlePublishDateTime"
                          >
                          </v-time-picker>
                        </v-tab-item>
                      </v-tabs-items>
                    </v-card>
                  </v-dialog>
                </v-col>

                <v-col cols="12">
                  <v-combobox
                    v-model="editedItemComputedTags"
                    label="Tags"
                    multiple
                    chips
                    append-icon=""
                    :items="productTagNameArray"
                    deletable-chips
                    clearable
                  ></v-combobox>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-menu
                    v-model="editProductBundleCategoriesMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    :nudge-bottom="-12"
                    :rounded="false"
                  >
                    <template v-slot:activator="{ on }">
                      <div @click="toggleEditProductBundleMenu">
                        <v-combobox
                          class="pt-2"
                          :value="computedEditProductBundleCategoriesValue"
                          label="Categories"
                          :rules="formRules.category"
                          required
                          clearable
                          v-on="on"
                          readonly
                          @click:clear="editedItem.categoryId = ''"
                        ></v-combobox>
                      </div>
                    </template>
                    <v-card max-height="200px" class="overflow-y-auto">
                      <v-card-text>
                        <v-treeview
                          :items="categoryTree"
                          color="primary"
                          activatable
                          item-key="id"
                          @update:active="updateProductBundleCategory"
                          :active="[editedItem.categoryId]"
                        ></v-treeview>
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-autocomplete
                    v-model="editedItem.subjectID"
                    class="pt-2"
                    :items="sortedSubjects"
                    :item-text="subjectLabel"
                    item-value="id"
                    :filter="subjectMatchesSearch"
                    :loading="loadingSubjects"
                    label="Subject"
                    hint='Search with any words, e.g. "4 maths"'
                    persistent-hint
                    clearable
                  ></v-autocomplete>
                </v-col>

                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="editedItem.videoPreview"
                    label="Video Preview"
                    hint='Eg. Vimeo: "1031522389", YouTube: "c_GorHGJ9Qg"'
                    :rules="formRules.validateVideoId"
                    clearable
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-autocomplete
                    v-model="editedItem.products"
                    :items="nonQuestionProductArray"
                    :disabled="!syncedProducts"
                    :loading="!syncedProducts"
                    item-text="title"
                    item-value="id"
                    :label="`${$i18n.t('headings.products')}*`"
                    required
                    return-object
                    :rules="formRules.products"
                    chips
                    clearable
                    deletable-chips
                    multiple
                  ></v-autocomplete>
                </v-col>

                <v-col cols="12">
                  <span class="text-h6">Description</span>
                  <RichTextEditor
                    v-model="editedItem.description"
                    :datamodelId="editedItem.id"
                    s3Folder="ProductBundles"
                    s3SubFolder="Description"
                  >
                  </RichTextEditor>
                </v-col>

                <v-col cols="12">
                  <v-card class="px-3 pt-3">
                    <v-row
                      no-gutters
                      justify="space-between"
                      style="gap: 12px"
                      class="pb-3"
                    >
                      <span class="text-h6">Product Bundle Images</span>
                      <v-btn
                        color="primary"
                        @click="uploadThumbnailImage"
                        :disabled="offline || editedIndex === -1 || loading"
                      >
                        {{
                          editedIndex === -1
                            ? "Save product bundle to add images"
                            : "Add Image"
                        }}
                      </v-btn>
                    </v-row>
                    <v-row
                      v-if="thumbnailImagesSrc?.length > 0"
                      no-gutters
                      class="thumbnailvrow"
                      style="margin-top: -12px"
                    >
                      <draggable
                        v-model="editedItem.thumbnails"
                        style="width: 100%"
                        class="d-flex flex-wrap"
                      >
                        <v-col
                          v-for="(
                            thumbnailKey, thumbnailIndex
                          ) in editedItem.thumbnails"
                          :key="thumbnailIndex"
                          cols="12"
                          sm="6"
                          md="4"
                          class="pa-3"
                        >
                          <div
                            @mouseenter="mouseOver(thumbnailIndex)"
                            @mouseleave="mouseOut"
                            class="d-flex justify-center align-center"
                            style="
                              position: relative;
                              border: thin solid;
                              border-radius: 4px;
                              height: 100%;
                              width: 100%;
                            "
                            :style="
                              productBundleImgHoveringIndex === thumbnailIndex
                                ? 'border-color: #ffa9b8;'
                                : ''
                            "
                          >
                            <v-img
                              max-height="100%"
                              max-width="100%"
                              :src="getThumbnailImageSrc(thumbnailKey)"
                              cover
                              style="border-radius: 4px"
                            ></v-img>
                            <!-- <img
                              style="
                                max-width: 100%;
                                max-height: 100%;
                                object-fit: cover;
                                border-radius: 8px;
                              "
                              :src="getThumbnailImageSrc(thumbnailKey)"
                            /> -->

                            <div
                              v-if="
                                productBundleImgHoveringIndex ===
                                  thumbnailIndex && editedIndex !== -1
                              "
                              style="position: absolute; top: 0; right: 0"
                            >
                              <v-menu
                                offset-x
                                v-model="isProductBundleImgMenuOpen"
                              >
                                <template
                                  v-slot:activator="{ on: menu, attrs }"
                                >
                                  <v-btn v-bind="attrs" icon v-on="{ ...menu }">
                                    <v-icon>mdi-cog</v-icon>
                                  </v-btn>
                                </template>
                                <div
                                  class="d-flex flex-column"
                                  style="background-color: white"
                                  @mouseleave="mouseOutMenu"
                                >
                                  <v-btn
                                    text
                                    color="red"
                                    @click="
                                      deleteProductBundleThumbnail(thumbnailKey)
                                    "
                                  >
                                    Remove
                                  </v-btn>
                                </div>
                              </v-menu>
                            </div>
                          </div>
                        </v-col>
                      </draggable>
                    </v-row>
                  </v-card>
                </v-col>

                <v-col cols="12">
                  <v-card class="px-3 pt-3">
                    <v-row
                      no-gutters
                      justify="space-between"
                      style="gap: 12px"
                      class="pb-3"
                    >
                      <span class="text-h6">Bundle Recommendations</span>
                      <v-btn
                        color="primary"
                        @click="createRecommendation"
                        :disabled="offline || loading"
                        >New Recommendation"
                      </v-btn>
                    </v-row>
                    <v-row v-if="editedRecommendations.length > 0" no-gutters>
                      <div style="width: 100%">
                        <v-data-table
                          :headers="recommendationHeaders"
                          :items="editedRecommendations"
                          no-data-text="No Recommendations"
                        >
                          <template v-slot:[`item.title`]="{ item }">
                            {{ getProductTitleById(item.productId) }}
                          </template>
                          <template
                            v-slot:[`item.discountPercentage`]="{ item }"
                          >
                            {{ formatPercentage(item.discountPercentage || 0) }}
                          </template>
                          <template v-slot:[`item.actions`]="{ item }">
                            <v-icon
                              small
                              class="mr-2"
                              @click="editRecommendation(item)"
                              :disabled="offline || loading"
                            >
                              mdi-pencil
                            </v-icon>
                            <v-icon
                              small
                              @click="deleteRecommendation(item)"
                              :disabled="offline || loading"
                            >
                              mdi-delete
                            </v-icon>
                          </template>
                        </v-data-table>
                      </div>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-btn
              color="primary"
              :disabled="editedIndex === -1 || productBundleSaved"
              @click="cancelWarning"
            >
              Cancel
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="close" :disabled="loading">
              Close
            </v-btn>
            <v-btn
              :disabled="!valid || productBundleSaved || offline || loading"
              :loading="loading"
              color="primary"
              @click="save"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="dialogCancel" max-width="500px">
      <v-card>
        <v-card-title class="justify-center" style="word-break: break-word"
          >Are you sure you want to discard the changes made to this product
          bundle?</v-card-title
        >
        <v-card-actions>
          <v-btn
            color="secondary"
            class="black--text"
            :disabled="cancelingChanges"
            @click="closeCancel"
            >Cancel</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn color="primary" :disabled="cancelingChanges" @click="cancel"
            >YES</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="dialogDelete" max-width="500px">
      <v-card>
        <v-card-title class="justify-center"
          >Are you sure you want to delete this product bundle?</v-card-title
        >
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" :disabled="deletingItem" @click="closeDelete"
            >Cancel</v-btn
          >
          <v-btn
            color="primary"
            :loading="deletingItem"
            :disabled="deletingItem"
            @click="deleteItemConfirm"
            >OK</v-btn
          >
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="dialogRecommendation" max-width="500px">
      <v-card>
        <v-form
          ref="recommendationForm"
          v-model="recommendationValid"
          @submit.prevent="saveRecommendation"
        >
          <v-card-title>
            <span class="text-h5">{{ recommendationFormTitle }}</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-autocomplete
                    v-model="editedRecommendation.productId"
                    label="Product"
                    :items="availableProductsAndBundles"
                    item-text="title"
                    item-value="id"
                    :search-input.sync="recommendationSearch"
                    :filter="recommendationFilter"
                    :rules="formRules.productId"
                    required
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedRecommendation.discountPercentage"
                    label="Discount Percentage"
                    type="number"
                    suffix="%"
                    :rules="formRules.discountPercentage"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="secondary"
              class="black--text"
              @click="closeRecommendation"
              >Close</v-btn
            >
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              @click="saveRecommendation"
              :disabled="!recommendationValid || loading"
              >Save</v-btn
            >
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="dialogDeleteRecommendation" max-width="500px">
      <v-card>
        <v-card-title class="justify-center"
          >Are you sure you want to delete this recommendation?</v-card-title
        >
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="deletingItem"
            @click="closeDeleteRecommendation"
            >Cancel</v-btn
          >
          <v-btn
            color="primary"
            :loading="deletingItem"
            :disabled="deletingItem"
            @click="deleteRecommendationConfirm"
            >OK</v-btn
          >
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="sharedSnackbar"
      min-width="auto"
      top
      elevation="10"
      timeout="2000"
      rounded="pill"
      color="secondary"
    >
      <div class="text-center">Product URL copied!</div>
    </v-snackbar>
    <v-snackbar
      v-model="copiedIDSnackbar"
      min-width="auto"
      top
      elevation="10"
      timeout="2000"
      rounded="pill"
      color="secondary"
    >
      <div class="text-center">Product ID copied!</div>
    </v-snackbar>

    <v-snackbar
      v-model="snackbar"
      min-width="auto"
      elevation="10"
      :color="snackbarColor"
      timeout="5000"
      top
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import {
  DataStore,
  Storage,
  SortDirection,
  API,
  // , Predicates
} from "aws-amplify";
import {
  Product,
  ProductTag,
  ProductCategory,
  ProductBundle,
  ProductSEO,
} from "@/models";
import _ from "lodash";
import draggable from "vuedraggable";
import RichTextEditor from "@/components/global-components/rich-text-editor/RichTextEditor.vue";
import store from "@/store";
import { reconfigureDatastore } from "@/plugins/datastore/config.js";
import * as mutations from "@/graphql/mutations";

const NO_SUBJECT = "__none__";

export default {
  name: "AdminHolidays",
  components: {
    draggable,
    RichTextEditor,
  },
  data: () => ({
    sharedSnackbar: false,
    copiedIDSnackbar: false,

    snackbar: false,
    snackbarText: "",
    snackbarColor: "success",

    initiallyLoaded: false,

    isProductBundleImgMenuOpen: false,
    productBundleImgHoveringIndex: -1,

    syncedProductBundles: false,
    syncedProductBundleArray: [],
    unfilteredProductBundlesArray: [],

    syncedProductTagArray: [],
    productTagNameArray: [],

    syncedProductCategoryArray: [],

    syncedProductSEOArray: [],
    syncedProducts: false,
    syncedProductArray: [],
    nonQuestionProductArray: [],

    selectedProductBundles: [],
    savingSelectedProductBundles: false,

    //Recommendations ------------------
    editedRecommendations: [],
    unEditedRecommendations: [],
    dialogRecommendation: false,
    editedRecommendation: { productId: null, discountPercentage: 0 },
    defaultRecommendation: { productId: null, discountPercentage: 0 },
    editedRecommendationIndex: -1,
    recommendationValid: false,
    recommendationSearch: "",
    dialogDeleteRecommendation: false,
    recommendationHeaders: [
      { text: "Product Title", value: "title" },
      { text: "Discount %", value: "discountPercentage" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    //Recommendations-END ------------------

    //Bulk Edit ------------------
    bulkEditLoading: null,
    expBulkEditPanels: 0,
    bulkEditAll: false,

    bulkEditPublishDate: "",
    bulkEditPublishTime: "",
    bulkEditPublishDateTime: null,
    bulkEditDateTimeDialog: false,
    bulkEditDateTimeTab: "date",

    bulkEditPriceChangePercent: null,
    bulkEditPriceChangeValue: null,

    bulkEditDiscountPercent: null,
    //Bulk Edit-END ------------------

    productBundlesDataTableSearchSubjects: [],

    loading: false,
    valid: false,

    datetimeDialog: false,
    dateTimeTab: "date",
    productBundlePublishDate: "",
    productBundlePublishTime: "",

    dialog: false,
    dialogDelete: false,
    dialogCancel: false,

    deletingItem: false,
    cancelingChanges: false,

    sortBy: "title",
    sortDesc: true,
    productBundlesDataTableSearch: "",
    productBundlesDataTableSearchTags: [],
    productBundlesDataTableSearchCategories: [],
    productBundlesDataTablePageCount: 1,
    productBundlesDataTableRowsPerPage: 10,
    productBundlesDataTableServerItemsLength: 0,
    searchProductBundleCategoriesMenu: false,
    editProductBundleCategoriesMenu: false,
    editCategoryParentMenu: false,
    showOnlySoftDeleted: false,
    showPriceMismatches: false,

    headers: [
      { text: "Title", value: "title" },
      { text: "Featured", value: "featured" },
      { text: "Price", value: "price" },
      { text: "Discount", value: "discountPercentage" },
      {
        text: "Category",
        value: "categoryId",
        sortable: false,
      },
      { text: "Publish Date", value: "publishDate" },
      { text: "Modified Date", value: "updatedAt" },
      {
        text: "Soft Deleted",
        value: "softDeleted",
        sortable: false,
      },
      { text: "Actions", value: "actions", sortable: false },
    ],

    productBundles: [],
    thumbnailImagesSrc: [],

    editedIndex: -1,

    unEditedProductBundle: {},

    editedItem: {
      title: "",
      description: "",
      price: null,
      discountPercentage: null,
      thumbnails: [],
      tags: [],
      products: [],
      publishDate: null,
      featured: false,
      searchField: "",
      categoryId: "",
      subjectID: null,
      videoPreview: "",
      recommendations: [],
    },
    defaultItem: {
      title: "",
      description: "",
      price: null,
      discountPercentage: null,
      thumbnails: [],
      tags: [],
      products: [],
      publishDate: null,
      featured: false,
      searchField: "",
      categoryId: "",
      subjectID: null,
      videoPreview: "",
      recommendations: [],
    },

    formRules: {
      productId: [(v) => !!v || "Product is required"],
      discountPercentage: [
        (v) => (v >= 0 && v <= 100) || "Discount must be between 0 and 100",
      ],
      title: [(v) => !!v || "Title is required"],
      hint: [(v) => !!v || "Hint is required"],
      placeholder: [(v) => !!v || "Placeholder is required"],
      label: [(v) => !!v || "Label is required"],
      validateVideoId: [
        // (v) =>
        //   (Array.isArray(v) && v.length > 0) ||
        //   "At least one video ID is required", // Ensure array is not empty
        (v) => {
          if (!v) return true;

          const youtubeRegex = /^[a-zA-Z0-9_-]{11}$/; // YouTube ID regex
          const vimeoRegex = /^[0-9]+$/; // Vimeo ID regex

          if (!Array.isArray(v)) {
            return (
              youtubeRegex.test(v) ||
              vimeoRegex.test(v) ||
              "Video ID is invalid. Use a YouTube or Vimeo ID."
            );
          }

          return (
            v?.every((id) => {
              return youtubeRegex.test(id) || vimeoRegex.test(id);
            }) || "One or more video IDs are invalid. Use YouTube or Vimeo IDs."
          );
        },
      ],
    },

    productBundleSearchExcludeFields: [
      "id",
      "thumbnails",
      "searchField",
      "categoryId",
      "subjectID",
      "createdAt",
      "featured",
      "publishDate",
      "updatedAt",
      "_version",
      "_lastChangedAt",
      "_deleted",
    ],
  }),

  computed: {
    ...mapState({
      // subjectsLoaded: (state) => state.subjectsLoaded,
      loadingSubjects: (state) => state.loadingSubjects,
      offline: (state) => !state.isOnline,
      datastorePredicates: (state) => state.datastorePredicates,
      changingDatastoreConfig: (state) => state.changingDatastoreConfig,
      datastoreReady: (state) => state.datastoreReady,
      startedDatastoreSync: (state) => state.startedDatastoreSync,
      productBundleParams: (state) => state.productBundleParams,
      userGroups: (state) => state.userGroups,
    }),

    ...mapGetters(["sortedSubjects"]),

    // "No subject assigned" comes first so you can find everything still
    // waiting to be tagged for school packages.
    subjectFilterItems() {
      return [
        { id: NO_SUBJECT, grade: null, subject: null },
        ...this.sortedSubjects,
      ];
    },

    subjectsByID() {
      const lookup = {};
      for (const subject of this.sortedSubjects) {
        lookup[subject.id] = subject;
      }
      return lookup;
    },

    recommendationFormTitle() {
      return this.editedRecommendationIndex === -1
        ? "New Recommendation"
        : "Edit Recommendation";
    },

    availableProductsAndBundles() {
      return [
        ...this.syncedProductArray,
        ...this.unfilteredProductBundlesArray,
      ].filter(
        (p) =>
          p.id !== this.editedItem.id &&
          !this.editedRecommendations.some(
            (r, i) =>
              r.productId === p.id && i !== this.editedRecommendationIndex,
          ),
      );
    },

    categoryTree() {
      return this.buildCategoryTree(this.syncedProductCategoryArray);
    },

    computedEditProductBundleCategoriesValue: {
      get: function () {
        const category = this.syncedProductCategoryArray.find(
          (category) => category.id === this.editedItem.categoryId,
        );

        if (category) {
          let parentCategories = [];

          if (category.parentId) {
            let currentCategory = category;
            while (currentCategory.parentId) {
              const parentCategory = this.syncedProductCategoryArray.find(
                (category) => category.id === currentCategory.parentId,
              );
              if (parentCategory) {
                parentCategories.unshift(parentCategory.name);
                currentCategory = parentCategory;
              } else {
                break;
              }
            }
          }

          return parentCategories.length > 0
            ? parentCategories.join("/") + "/" + category.name
            : category.name;
        } else return "";
      },
    },

    editedItemComputedTags: {
      get: function () {
        const editedItemTags = this.editedItem.tags
          ? this.editedItem.tags
              .map((tagId) => {
                const productBundleTag = this.syncedProductTagArray.find(
                  (tag) => tag.id === tagId,
                );

                if (productBundleTag) return productBundleTag.name;
              })
              .filter((tag) => tag !== undefined)
          : [];
        return editedItemTags;
      },
      set: async function (newValue) {
        const updatedTags = [];
        for (const tag of newValue) {
          if (typeof tag === "string") {
            const existingTag = this.syncedProductTagArray.find(
              (t) => t.name === tag,
            );
            if (existingTag) {
              // If tag already exists, push its ID
              updatedTags.push(existingTag.id);
            } else {
              // If tag doesn't exist, create a new ProductTag model
              try {
                const newTag = await DataStore.save(
                  new ProductTag({ name: tag }),
                );
                this.productTagNameArray.push(newTag.name);
                updatedTags.push(newTag.id);
              } catch (error) {
                console.error("Error saving new tag:", error);
              }
            }
          }
        }

        this.editedItem.tags = updatedTags;
      },
    },

    productBundlesSearchTags: {
      get: function () {
        let tags = [];
        _.forEach(this.productBundlesDataTableSearchTags, (tagId) => {
          const productBundleTag = _.find(this.syncedProductTagArray, {
            id: tagId,
          });
          if (productBundleTag) {
            tags.push(productBundleTag);
          }
        });
        return tags;
      },
      set: function (newValue) {
        this.productBundlesDataTableSearchTags = newValue;
      },
    },

    computedProductBundlesSearchCategories: {
      get: function () {
        let categories = [];
        _.forEach(
          this.productBundlesDataTableSearchCategories,
          (categoryId) => {
            const category = _.find(this.syncedProductCategoryArray, {
              id: categoryId,
            });
            if (category) {
              let parentCategories = [];

              if (category.parentId) {
                let currentCategory = category;
                while (currentCategory.parentId) {
                  const parentCategory = this.syncedProductCategoryArray.find(
                    (category) => category.id === currentCategory.parentId,
                  );
                  if (parentCategory) {
                    parentCategories.unshift(parentCategory.name);
                    currentCategory = parentCategory;
                  } else {
                    break;
                  }
                }
              }

              categories.push(
                parentCategories.length > 0
                  ? parentCategories.join("/") + "/" + category.name
                  : category.name,
              );
            }
          },
        );
        return categories;
      },
    },

    formTitle() {
      return this.editedIndex === -1
        ? "New Product Bundle"
        : "Edit Product Bundle";
    },

    formattedProductBundlePublishDate() {
      return this.editedItem.publishDate
        ? new Date(this.editedItem.publishDate).toLocaleString()
        : "";
    },

    formattedBulkEditPublishDate() {
      return this.bulkEditPublishDateTime
        ? new Date(this.bulkEditPublishDateTime).toLocaleString()
        : "";
    },

    hasMismatchedProductBundles() {
      return this.productBundles.some((bundle) =>
        this.bundlePriceMismatch(bundle),
      );
    },

    productBundleSaved() {
      const recommendationsEqual = _.isEqual(
        this.editedRecommendations,
        this.unEditedRecommendations,
      );
      const equal = _.isEqual(this.editedItem, this.unEditedProductBundle);

      return equal && recommendationsEqual;
    },

    totalProductsPrice() {
      if (this.editedItem.products.length === 0) {
        return "No products selected";
      }

      const total = this.editedItem.products.reduce((sum, productObj) => {
        const product = this.syncedProductArray.find(
          (p) => p.id === productObj.id,
        );
        if (product) {
          let price = product.price || 0;
          if (product.discountPercentage) {
            price -= (price * product.discountPercentage) / 100;
          }
          return sum + price;
        }
        return sum;
      }, 0);
      return total.toFixed(2);
    },

    productBundleVat() {
      return this.editedItem.price
        ? Number.parseFloat(
            (this.editedItem.price / (1 + 0.15) - this.editedItem.price) * -1,
          ).toFixed(2)
        : 0;
    },
  },

  async mounted() {
    try {
      store.commit("changeDatastorePredicates", [
        {
          model: "Product",
          predicate: "Admin",
        },
        {
          model: "ProductSEO",
          predicate: "Admin",
        },
        {
          model: "ProductBundle",
          predicate: "Admin",
        },
        {
          model: "ProductTag",
          predicate: "Admin",
        },
        {
          model: "ProductCategory",
          predicate: "Admin",
        },
      ]);

      if (!this.startedDatastoreSync || this.datastoreReady) {
        reconfigureDatastore();
      }

      store.dispatch("fetchSubjects");

      (this.formRules.title = [
        (v) => !!v || "Title is required",
        (v) =>
          (v && v.trim().length >= 3) || "Title must be at least 3 characters",
        (v) => {
          if (!v || !this.editedItem.categoryId) return true;
          const titleCategoryCombo = `${v.trim().toLowerCase()}_${
            this.editedItem.categoryId
          }`;
          const duplicateProduct = this.syncedProductArray.find(
            (item) =>
              item.id !== this.editedItem.id &&
              `${item.title.trim().toLowerCase()}_${item.categoryId}` ===
                titleCategoryCombo,
          );
          const duplicateBundle = this.unfilteredProductBundlesArray.find(
            (item) =>
              item.id !== this.editedItem.id &&
              `${item.title.trim().toLowerCase()}_${item.categoryId}` ===
                titleCategoryCombo,
          );
          if (duplicateProduct) {
            return "This title and category combination is already in use by a Product";
          }
          if (duplicateBundle) {
            return "This title and category combination is already in use by a Product Bundle";
          }
          return true;
        },
      ]),
        (this.headers = [
          {
            text: "Title",
            value: "title",
          },
          { text: "Featured", value: "featured" },
          {
            text: "Price",
            value: "price",
          },
          {
            text: "Discount",
            value: "discountPercentage",
          },
          {
            text: "Category",
            value: "categoryId",
            sortable: false,
          },
          { text: "Subject", value: "subjectID", sortable: false },
          {
            text: "Publish Date",
            value: "publishDate",
          },
          {
            text: "Modified Date",
            value: "updatedAt",
          },
          {
            text: "Soft Deleted",
            value: "softDeleted",
            sortable: false,
          },
          { text: "Actions", value: "actions", sortable: false },
        ]);

      if (!this.changingDatastoreConfig) {
        await this.syncData();
      }
    } catch (error) {
      console.log(error);
    } finally {
      if (!this.initiallyLoaded) {
        this.initiallyLoaded = true;
      }
    }
  },

  watch: {
    changingDatastoreConfig: {
      async handler(newVal, oldVal) {
        if (newVal === true) {
          return;
        }

        if (newVal === false) {
          await this.syncData();
        }
      },
    },

    productBundleParams: {
      handler(val) {
        console.log("productBundleParams", val);
        if (val) {
          this.createItem();
        }
      },
      immediate: true,
    },

    dialog(val) {
      val || this.close();
    },

    dialogDelete(val) {
      val || this.closeDelete();
    },

    sortDesc() {
      this.queryProductBundles();
    },

    sortBy() {
      this.queryProductBundles();
    },
  },

  methods: {
    subjectLabel(subject) {
      if (!subject) return "";
      if (subject.id === NO_SUBJECT) return "No subject assigned";
      return `Gr.${subject.grade} - ${subject.subject}`;
    },

    // Every word typed must appear somewhere in the grade or subject name,
    // in any order. "4 maths", "maths 4" and "4 mat" all work.
    subjectMatchesSearch(subject, searchText) {
      const subjectText = this.subjectLabel(subject).toLowerCase();
      const searchWords = (searchText || "").toLowerCase().split(" ");

      return searchWords.every((word) => subjectText.includes(word));
    },

    async loadSubjects() {
      this.loadingSubjects = true;
      try {
        let allSubjects = [];
        let nextToken = null;

        do {
          const result = await API.graphql({
            query: /* GraphQL */ `
              query ListSubjectsForProducts($nextToken: String) {
                listSubjects(limit: 1000, nextToken: $nextToken) {
                  items {
                    id
                    grade
                    subject
                    _deleted
                  }
                  nextToken
                }
              }
            `,
            variables: { nextToken },
            authMode: "AMAZON_COGNITO_USER_POOLS",
          });

          allSubjects = allSubjects.concat(result.data.listSubjects.items);
          nextToken = result.data.listSubjects.nextToken;
        } while (nextToken);

        this.subjects = allSubjects.filter(
          (subject) => subject && !subject._deleted,
        );
      } catch (error) {
        console.log("loadSubjects error:", error);
      } finally {
        this.loadingSubjects = false;
      }
    },

    async updateReciprocalRecommendation(currentId, rec) {
      const { productId, discountPercentage } = rec;
      let otherModel, other;
      other = await DataStore.query(Product, productId).catch(() => null);
      if (other) {
        otherModel = Product;
      } else {
        other = await DataStore.query(ProductBundle, productId).catch(
          () => null,
        );
        if (other) {
          otherModel = ProductBundle;
        } else {
          console.error(`No product or bundle found with id ${productId}`);
          return;
        }
      }
      let otherRecs = other.recommendations
        ? _.cloneDeep(other.recommendations)
        : [];
      // console.log("otherRecs before update:", JSON.stringify(otherRecs));
      const existingIndex = otherRecs.findIndex(
        (r) => r.productId === currentId,
      );
      // console.log("existingIndex:", existingIndex);
      if (existingIndex !== -1) {
        otherRecs[existingIndex].discountPercentage = discountPercentage;
        // console.error(`Recommendation already exists for product ${productId}`);
        // return;
      } else {
        otherRecs.push({ productId: currentId, discountPercentage });
      }
      // console.log("otherRecs after update:", JSON.stringify(otherRecs));
      await DataStore.save(
        otherModel.copyOf(other, (updated) => {
          updated.recommendations = otherRecs.length > 0 ? otherRecs : null;
        }),
      );
    },

    recommendationFilter(item, queryText, itemText) {
      const searchText = queryText.toLowerCase();
      return (
        item.title.toLowerCase().includes(searchText) ||
        item.id.includes(searchText)
      );
    },

    getProductTitleById(id) {
      const product = this.unfilteredProductBundlesArray.find(
        (p) => p.id === id,
      );
      return product
        ? product.title
        : this.syncedProductArray.find((p) => p.id === id)?.title ||
            "Unknown Product";
    },

    createRecommendation() {
      this.editedRecommendation = _.cloneDeep(this.defaultRecommendation);
      this.editedRecommendationIndex = -1;
      this.dialogRecommendation = true;
    },

    editRecommendation(item) {
      this.editedRecommendationIndex = this.editedRecommendations.indexOf(item);
      this.editedRecommendation = _.cloneDeep(item);
      this.dialogRecommendation = true;
    },

    deleteRecommendation(item) {
      this.editedRecommendationIndex = this.editedRecommendations.indexOf(item);
      this.dialogDeleteRecommendation = true;
    },

    deleteRecommendationConfirm() {
      this.deletingItem = true;

      if (this.editedRecommendationIndex > -1) {
        this.editedRecommendations.splice(this.editedRecommendationIndex, 1);
      }

      this.deletingItem = false;
      this.closeDeleteRecommendation();
    },

    closeDeleteRecommendation() {
      this.dialogDeleteRecommendation = false;
      this.editedRecommendationIndex = -1;
    },

    saveRecommendation() {
      if (this.editedRecommendationIndex > -1) {
        this.$set(
          this.editedRecommendations,
          this.editedRecommendationIndex,
          _.cloneDeep(this.editedRecommendation),
        );
      } else {
        this.editedRecommendations.push(_.cloneDeep(this.editedRecommendation));
      }

      this.closeRecommendation();
    },

    closeRecommendation() {
      this.dialogRecommendation = false;
      this.$nextTick(() => {
        this.editedRecommendation = _.cloneDeep(this.defaultRecommendation);
        this.editedRecommendationIndex = -1;
      });
    },

    getFilteredProductsAndBundles() {
      const searchTerms = this.productBundlesDataTableSearch
        .split(" ")
        .map((str) => str.toLowerCase());

      const filterFn = (p) => {
        return (
          (this.productBundlesDataTableSearchSubjects.length === 0 ||
            this.productBundlesDataTableSearchSubjects.some((subjectID) =>
              subjectID === NO_SUBJECT ? !p.subjectID : p.subjectID === subjectID,
            )) &&
          this.productBundlesDataTableSearchTags.every((tag) =>
            p.tags?.includes(tag),
          ) &&
          (this.productBundlesDataTableSearchCategories.length === 0 ||
            this.productBundlesDataTableSearchCategories.includes(
              p.categoryId,
            )) &&
          searchTerms.every((term) => p.searchField?.includes(term))
        );
      };

      const filteredBundles =
        this.unfilteredProductBundlesArray.filter(filterFn);

      return [...filteredBundles];
    },

    async bulkUpdateProducts(updates) {
      try {
        store.commit("removeSubscriptions", ["Product", "ProductBundle"]);

        const CHUNK_SIZE = 200;
        const chunks = _.chunk(updates, CHUNK_SIZE);
        for (const chunk of chunks) {
          try {
            const response = await API.post(
              "productactionsapi",
              "/item/bulk-update-products",
              {
                body: { updates: chunk },
              },
            );

            console.log("response", response);

            if (response.success) {
              this.snackbarText = `Successfully updated ${response.updatedCount} bundles!`;
              this.snackbarColor = "success";
              this.snackbar = true;
            } else {
              this.snackbarText = `Bulk update failed: ${response.errors.join(
                "; ",
              )}`;
              this.snackbarColor = "error";
              this.snackbar = true;
            }
          } catch (error) {
            console.error("Bulk update chunk failed:", error);
          }
        }
      } catch (error) {
        console.error("Error in bulkUpdateProducts:", error);
      } finally {
        console.log("Finished bulk updating products, resyncing data.");
        this.syncProducts();
        this.syncProductBundles();
      }
    },

    copyProductID(product) {
      // Use the Web Share API to share a message, image, and URL
      if (navigator.share) {
        navigator
          .share({
            // title: heading,
            text: product.id,
            // url: shareUrl,
          })
          .then(() => {
            // console.log("Shared successfully.")
          })
          .catch(() => {
            // console.error("Error sharing:", error)
          });

        // Use the Clipboard API to copy the custom string to the clipboard
        navigator.clipboard.writeText(product.id).catch(() => {
          // console.error('Error copying to clipboard:', error);
        });
      } else {
        // Use the Clipboard API to copy the custom string to the clipboard
        navigator.clipboard.writeText(product.id).catch(() => {
          // console.error('Error copying to clipboard:', error);
        });
      }

      this.copiedIDSnackbar = true;
    },

    shareProduct(product) {
      const existingProductSEO =
        this.syncedProductSEOArray.find(
          (seo) => seo.productBundleID === product.id,
        ) || null;

      if (!existingProductSEO) return;

      const shareUrl = `${window.location.origin}/${existingProductSEO.seoId}`;

      // Use the Web Share API to share a message, image, and URL
      if (navigator.share) {
        navigator
          .share({
            // title: heading,
            // text: message,
            url: shareUrl,
          })
          .then(() => {
            // console.log("Shared successfully.")
          })
          .catch(() => {
            // console.error("Error sharing:", error)
          });

        // Use the Clipboard API to copy the custom string to the clipboard
        navigator.clipboard.writeText(shareUrl).catch(() => {
          // console.error('Error copying to clipboard:', error);
        });
      } else {
        // Use the Clipboard API to copy the custom string to the clipboard
        navigator.clipboard.writeText(shareUrl).catch(() => {
          // console.error('Error copying to clipboard:', error);
        });
      }

      this.sharedSnackbar = true;
    },

    async createProductBundleSEOs() {
      try {
        this.loading = true;

        // Create a Map for faster lookups of existing ProductSEOs by productBundleID
        const existingSEOMap = new Map(
          this.syncedProductSEOArray.map((seo) => [seo.productBundleID, seo]),
        );

        // Filter product bundles without SEOs using Map for O(1) lookup
        const productBundlesWithoutSEOs =
          this.unfilteredProductBundlesArray.filter(
            (productB) => !existingSEOMap.has(productB.id),
          );

        console.log(
          `Total product bundles without SEOs: ${productBundlesWithoutSEOs.length}`,
        );

        // Create a Map for category lookups
        const categoryMap = new Map(
          this.syncedProductCategoryArray.map((cat) => [cat.id, cat]),
        );

        // Chunk the array into groups of 10
        const chunks = (arr, size) =>
          Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
            arr.slice(i * size, i * size + size),
          );

        const productChunks = chunks(productBundlesWithoutSEOs, 10);

        // Helper function to delay execution
        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

        // Process chunks sequentially with a delay
        for (const chunk of productChunks) {
          const promises = chunk.map(async (product, i) => {
            console.log(
              `Processing product bundle ${i + 1} of ${chunk.length} in chunk`,
            );

            const productCategory = categoryMap.get(product.categoryId) || null;

            const seoId = `product/${this.generateSeoId(
              product.title,
              productCategory,
            )}`;
            if (!seoId) {
              console.warn(
                `Failed to generate seoId for product bundle: ${product.id}`,
              );
              return;
            }

            const productSEODetails = {
              seoId,
              productBundleID: product.id,
            };

            try {
              await API.graphql({
                query: mutations.createProductSEO,
                variables: { input: productSEODetails },
                authMode: "AMAZON_COGNITO_USER_POOLS",
              });
            } catch (err) {
              console.error(
                `Error creating ProductSEO for product bundle ${product.id}:`,
                err,
              );
            }
          });

          // Run chunk promises concurrently
          await Promise.all(promises);

          // Add a short delay between chunks to prevent overload (e.g., 100ms)
          await delay(100);
        }

        console.log("Done creating ProductBundleSEOs");
      } catch (error) {
        console.error("createProductBundleSEOs error:", error);
      } finally {
        this.loading = false;
      }
    },

    async changeProductBundlesSearchQuery() {
      await this.queryProductBundles();
    },

    // Helper function to recursively collect category IDs
    collectCategoryIds(categoryId) {
      let ids = [];

      // Helper function to traverse categories
      function traverseCategories(currentId) {
        // Add the current category ID to the list
        ids.push(currentId);
        // Find all children whose parentId matches currentId
        const children = this.syncedProductCategoryArray.filter(
          (cat) => cat.parentId === currentId,
        );
        // Recursively traverse each child
        children.forEach((child) => traverseCategories.call(this, child.id));
      }

      traverseCategories.call(this, categoryId);

      return ids;
    },

    formatDateTime(date) {
      if (!date) return "";
      return new Date(date).toISOString().replace("T", " ").split("Z")[0];
    },

    slugify(text) {
      return text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
    },

    generateSeoId(productTitle, productCategory) {
      try {
        if (!productCategory) {
          console.warn(
            "Category not found for ID:",
            this.editedItem.categoryId,
          );
          return this.slugify(productTitle || "untitled");
        }

        // Build category slug hierarchy
        let categorySlugs = [
          productCategory.slug || this.slugify(productCategory.name),
        ];
        let currentCategory = productCategory;
        while (currentCategory.parentId) {
          const parentCategory = this.syncedProductCategoryArray.find(
            (cat) => cat.id === currentCategory.parentId,
          );
          if (!parentCategory) break;
          categorySlugs.unshift(
            parentCategory.slug || this.slugify(parentCategory.name),
          );
          currentCategory = parentCategory;
        }

        // Combine category slugs and product title
        const seoIdParts = [
          ...categorySlugs,
          this.slugify(productTitle || "untitled"),
        ];
        return seoIdParts.join("-");
      } catch (error) {
        console.error("Error generating seoId:", error);
        return "";
      }
    },

    async scheduleProductBundles() {
      this.bulkEditLoading = "schedule";

      try {
        let productsToUpdate = [];
        if (this.bulkEditAll) {
          productsToUpdate = this.getFilteredProductsAndBundles();
        } else {
          let productsToUpdate = this.unfilteredProductBundlesArray.filter(
            (p) => this.selectedProductBundles.some((sp) => sp.id === p.id),
          );
          // Existing DataStore logic for selected only
          for (const product of productsToUpdate) {
            await DataStore.save(
              ProductBundle.copyOf(product, (updated) => {
                updated.publishDate = this.bulkEditPublishDateTime;
                updated.searchField = JSON.stringify(
                  _.values(
                    _.omit(updated, this.productBundleSearchExcludeFields),
                  ),
                ).toLowerCase();
              }),
            );
          }
          return;
        }

        const updates = productsToUpdate.map((p) => ({
          id: p.id,
          bundle: true,
          publishDate: this.bulkEditPublishDateTime,
          searchField: JSON.stringify(
            _.values(_.omit(p, this.productBundleSearchExcludeFields)),
          ).toLowerCase(),
          _version: p._version,
        }));
        await this.bulkUpdateProducts(updates);
      } catch (error) {
        console.error(error);
      } finally {
        this.bulkEditLoading = null;
      }
    },

    async unScheduleProductBundles() {
      this.bulkEditLoading = "unschedule";
      try {
        let productsToUpdate = [];
        if (this.bulkEditAll) {
          productsToUpdate = this.getFilteredProductsAndBundles();
        } else {
          let productsToUpdate = this.unfilteredProductBundlesArray.filter(
            (p) => this.selectedProductBundles.some((sp) => sp.id === p.id),
          );
          // Existing DataStore logic for selected only
          for (const product of productsToUpdate) {
            await DataStore.save(
              ProductBundle.copyOf(product, (updated) => {
                updated.publishDate = null;
                updated.searchField = JSON.stringify(
                  _.values(
                    _.omit(updated, this.productBundleSearchExcludeFields),
                  ),
                ).toLowerCase();
              }),
            );
          }
          return;
        }

        const updates = productsToUpdate.map((p) => ({
          id: p.id,
          bundle: true,
          publishDate: null,
          searchField: JSON.stringify(
            _.values(_.omit(p, this.productBundleSearchExcludeFields)),
          ).toLowerCase(),
          _version: p._version,
        }));
        await this.bulkUpdateProducts(updates);
      } catch (error) {
        console.error(error);
      } finally {
        this.bulkEditLoading = null;
      }
    },

    async changeProductBundlesPrice(direction) {
      this.bulkEditLoading = `${direction}_price`;

      try {
        let productsToUpdate = [];
        if (this.bulkEditAll) {
          productsToUpdate = this.getFilteredProductsAndBundles();
        } else {
          let productsToUpdate = this.unfilteredProductBundlesArray.filter(
            (p) => this.selectedProductBundles.some((sp) => sp.id === p.id),
          );
          // Existing DataStore logic for selected only
          const changeValue = this.bulkEditPriceChangeValue
            ? parseFloat(this.bulkEditPriceChangeValue)
            : 0;
          const changePercent = this.bulkEditPriceChangePercent
            ? parseFloat(this.bulkEditPriceChangePercent) / 100
            : 0;
          const sign = direction === "increase" ? 1 : -1;
          for (const product of productsToUpdate) {
            let newPrice = product.price;
            if (changeValue) {
              newPrice += sign * changeValue;
            } else if (changePercent) {
              newPrice *= 1 + sign * changePercent;
            }
            newPrice = Math.round(newPrice * 100) / 100;
            await DataStore.save(
              ProductBundle.copyOf(product, (updated) => {
                updated.price = newPrice;
                updated.searchField = JSON.stringify(
                  _.values(
                    _.omit(updated, this.productBundleSearchExcludeFields),
                  ),
                ).toLowerCase();
              }),
            );
          }
          return;
        }
        const changeValue = this.bulkEditPriceChangeValue
          ? parseFloat(this.bulkEditPriceChangeValue)
          : 0;
        const changePercent = this.bulkEditPriceChangePercent
          ? parseFloat(this.bulkEditPriceChangePercent) / 100
          : 0;
        const sign = direction === "increase" ? 1 : -1;

        const updates = productsToUpdate.map((p) => {
          let newValue = p.price;
          if (changeValue) {
            newValue += sign * changeValue;
          } else if (changePercent) {
            newValue *= 1 + sign * changePercent;
          }
          newValue = Math.round(newValue * 100) / 100;

          return {
            id: p.id,
            bundle: true,
            price: newValue,
            searchField: JSON.stringify(
              _.values(_.omit(p, this.productBundleSearchExcludeFields)),
            ).toLowerCase(),
            _version: p._version,
          };
        });
        await this.bulkUpdateProducts(updates);
      } catch (error) {
        console.error(error);
      } finally {
        this.bulkEditLoading = null;
      }
    },

    async discountProductBundles() {
      this.bulkEditLoading = "discount";

      try {
        let productsToUpdate = [];
        if (this.bulkEditAll) {
          productsToUpdate = this.getFilteredProductsAndBundles();
        } else {
          let productsToUpdate = this.unfilteredProductBundlesArray.filter(
            (p) => this.selectedProductBundles.some((sp) => sp.id === p.id),
          );

          for (const product of productsToUpdate) {
            await DataStore.save(
              ProductBundle.copyOf(product, (updated) => {
                updated.discountPercentage =
                  (product.price || 0) === 0 ||
                  this.bulkEditDiscountPercent === "0"
                    ? null
                    : parseFloat(this.bulkEditDiscountPercent);
                updated.searchField = JSON.stringify(
                  _.values(
                    _.omit(updated, this.productBundleSearchExcludeFields),
                  ),
                ).toLowerCase();
              }),
            );
          }
          return;
        }

        const updates = productsToUpdate.map((p) => ({
          id: p.id,
          bundle: true,
          discountPercentage:
            (p.price || 0) === 0 || this.bulkEditDiscountPercent === "0"
              ? null
              : parseFloat(this.bulkEditDiscountPercent),
          searchField: JSON.stringify(
            _.values(_.omit(p, this.productBundleSearchExcludeFields)),
          ).toLowerCase(),
          _version: p._version,
        }));
        await this.bulkUpdateProducts(updates);
      } catch (error) {
        console.error(error);
      } finally {
        this.bulkEditLoading = null;
      }
    },

    async featureProductBundles() {
      this.bulkEditLoading = "feature";

      try {
        let productsToUpdate = [];
        if (this.bulkEditAll) {
          productsToUpdate = this.getFilteredProductsAndBundles();
        } else {
          let productsToUpdate = this.unfilteredProductBundlesArray.filter(
            (p) => this.selectedProductBundles.some((sp) => sp.id === p.id),
          );

          // Existing DataStore logic for selected only
          for (const product of productsToUpdate) {
            await DataStore.save(
              ProductBundle.copyOf(product, (updated) => {
                updated.featured = true;
                updated.searchField = JSON.stringify(
                  _.values(
                    _.omit(updated, this.productBundleSearchExcludeFields),
                  ),
                ).toLowerCase();
              }),
            );
          }
          return;
        }

        const updates = productsToUpdate.map((p) => ({
          id: p.id,
          bundle: true,
          featured: true,
          searchField: JSON.stringify(
            _.values(_.omit(p, this.productBundleSearchExcludeFields)),
          ).toLowerCase(),
          _version: p._version,
        }));
        await this.bulkUpdateProducts(updates);
      } catch (error) {
        console.error(error);
      } finally {
        this.bulkEditLoading = null;
      }
    },

    async unFeatureProductBundles() {
      this.bulkEditLoading = "unfeature";

      try {
        let productsToUpdate = [];
        if (this.bulkEditAll) {
          productsToUpdate = this.getFilteredProductsAndBundles();
        } else {
          let productsToUpdate = this.unfilteredProductBundlesArray.filter(
            (p) => this.selectedProductBundles.some((sp) => sp.id === p.id),
          );
          // Existing DataStore logic for selected only
          for (const product of productsToUpdate) {
            await DataStore.save(
              ProductBundle.copyOf(product, (updated) => {
                updated.featured = false;
                updated.searchField = JSON.stringify(
                  _.values(
                    _.omit(updated, this.productBundleSearchExcludeFields),
                  ),
                ).toLowerCase();
              }),
            );
          }
          return;
        }

        const updates = productsToUpdate.map((p) => ({
          id: p.id,
          bundle: true,
          featured: false,
          searchField: JSON.stringify(
            _.values(_.omit(p, this.productBundleSearchExcludeFields)),
          ).toLowerCase(),
          _version: p._version,
        }));
        await this.bulkUpdateProducts(updates);
      } catch (error) {
        console.error(error);
      } finally {
        this.bulkEditLoading = null;
      }
    },

    updateProductBundleCategory(val) {
      this.editedItem.categoryId = val.length > 0 ? val[0] : "";
    },

    toggleMenu() {
      this.searchProductBundleCategoriesMenu =
        !this.searchProductBundleCategoriesMenu;
    },

    toggleEditProductBundleMenu() {
      this.editProductBundleCategoriesMenu =
        !this.editProductBundleCategoriesMenu;
    },

    buildCategoryTree(categories, propCategoryId) {
      let tree = [];

      // Create a map of categories by their ID for quicker access
      const categoryMap = {};
      categories.forEach((category) => {
        categoryMap[category.id] = { ...category, children: [] };
      });

      // Function to recursively build the tree
      const buildTree = (categoryId) => {
        const category = categoryMap[categoryId];
        if (!category) return null; // Return null if category not found

        // Check if the category meets the condition to be added to the tree
        if (propCategoryId > "" && category.id === propCategoryId) {
          // Skip adding this category and its children
          return;
        }

        // Recursively build children
        if (category.parentId) {
          const parentCategory = categoryMap[category.parentId];
          if (parentCategory) {
            parentCategory.children.push(category);
          }
        } else {
          tree.push(category); // Add to top-level tree if no parent
        }
      };

      // Build tree for each category
      categories.forEach((category) => {
        buildTree(category.id);
      });

      return tree;
    },

    updateProductBundlePublishDateTime(action) {
      if (action === "clear") {
        this.productBundlePublishDate = null;
        this.productBundlePublishTime = null;
        this.editedItem.publishDate = null;
      } else if (
        this.productBundlePublishDate &&
        this.productBundlePublishTime
      ) {
        this.editedItem.publishDate = new Date(
          new Date(
            this.productBundlePublishDate +
              "T" +
              this.productBundlePublishTime +
              ":00Z",
          ).getTime() -
            2 * 60 * 60 * 1000,
        ).toISOString();
      } else {
        this.editedItem.publishDate = null;
      }
    },

    updateBulkEditPublishDateTime(action) {
      if (action === "clear") {
        this.bulkEditPublishDate = null;
        this.bulkEditPublishTime = null;
        this.bulkEditPublishDateTime = null;
      } else if (this.bulkEditPublishDate && this.bulkEditPublishTime) {
        this.bulkEditPublishDateTime = new Date(
          new Date(
            this.bulkEditPublishDate + "T" + this.bulkEditPublishTime + ":00Z",
          ).getTime() -
            2 * 60 * 60 * 1000,
        ).toISOString();
      } else {
        this.bulkEditPublishDateTime = null;
      }
    },

    closeDateTimeDialog() {
      this.datetimeDialog = false;
      this.dateTimeTab = "date";
    },

    closeBulkEditDateTimeDialog() {
      this.bulkEditDateTimeDialog = false;
      this.bulkEditDateTimeTab = "date";
    },

    cancelWarning() {
      this.dialogCancel = true;
    },

    productBundlesDataTablePageCountUpdate(val) {
      if (val !== 0) {
        this.productBundlesDataTablePageCount = val; // Convert to 0-based index
        this.queryProductBundles();
      }
    },

    formatPercentage(percent) {
      // Ensure the price is a valid number
      if (percent === null || percent === undefined || isNaN(percent)) {
        return "";
      }

      return `${percent}%`;
    },

    formatPrice(price) {
      // Ensure the price is a valid number
      if (price === null || price === undefined || isNaN(price)) {
        return "";
      }

      // Convert the price to a fixed-point notation with 2 decimals
      const formattedPrice = parseFloat(price).toFixed(2);

      return `R ${formattedPrice}`;
    },

    formatCategoryId(categoryId) {
      const category = this.syncedProductCategoryArray.find(
        (category) => category.id === categoryId,
      );

      if (category) {
        let parentCategories = [];

        if (category.parentId) {
          let currentCategory = category;
          while (currentCategory.parentId) {
            const parentCategory = this.syncedProductCategoryArray.find(
              (category) => category.id === currentCategory.parentId,
            );
            if (parentCategory) {
              parentCategories.unshift(parentCategory.name);
              currentCategory = parentCategory;
            } else {
              break;
            }
          }
        }

        return parentCategories.length > 0
          ? parentCategories.join("/") + "/" + category.name
          : category.name;
      } else return "";
    },

    async deleteProductBundleThumbnail(thumbnailKey) {
      // console.log("deleteProductBundleThumbnail:", thumbnailKey);
      await Storage.remove(thumbnailKey, {
        level: "public",
      })
        .then(() => {
          const mutableThumbnails = _.cloneDeep(this.editedItem.thumbnails);
          _.remove(
            mutableThumbnails,
            (thumbnail) => thumbnail === thumbnailKey,
          );
          this.editedItem.thumbnails = mutableThumbnails;

          _.remove(
            this.thumbnailImagesSrc,
            (thumbnail) => thumbnail.key === thumbnailKey,
          );

          this.editedItem.searchField = JSON.stringify(
            _.values(
              _.omit(this.editedItem, this.productBundleSearchExcludeFields),
            ),
          ).toLowerCase();

          DataStore.save(
            ProductBundle.copyOf(
              _.find(this.unfilteredProductBundlesArray, {
                id: this.editedItem.id,
              }),
              (productBundle) => {
                productBundle.thumbnails = this.editedItem.thumbnails;
                productBundle.searchField = this.editedItem.searchField;
              },
            ),
          );
        })
        .catch((err) => console.log(err));
    },

    mouseOver(index) {
      if (!this.isProductBundleImgMenuOpen) {
        this.productBundleImgHoveringIndex = index;
      }
    },

    mouseOut() {
      if (!this.isProductBundleImgMenuOpen) {
        this.productBundleImgHoveringIndex = -1;
      }
    },

    mouseOutMenu() {
      this.isProductBundleImgMenuOpen = false;
      this.productBundleImgHoveringIndex = -1;
    },

    getThumbnailImageSrc(thumbnailKey) {
      const thumbnailImgSrc = _.find(this.thumbnailImagesSrc, {
        key: thumbnailKey,
      });
      return thumbnailImgSrc ? thumbnailImgSrc.src : "";
    },

    async getThumbnailImage(productBundleThumbnailkey) {
      const thumbnailImgSrc = await Storage.get(productBundleThumbnailkey, {
        level: "public",
      });
      return thumbnailImgSrc;
    },

    async uploadThumbnailImage() {
      try {
        const inputElement = document.createElement("input");
        inputElement.type = "file";
        inputElement.accept = "image/*";
        // inputElement.multiple = true; // Allow multiple files to be selected

        inputElement.addEventListener("change", async (event) => {
          if (
            event.target instanceof HTMLInputElement &&
            event.target.files &&
            event.target.files.length > 0
          ) {
            this.loading = true;
            const file = event.target.files[0];
            const maxSize = 800; // Define your maximum size here

            // Create a function to resize the image
            const resizeImage = (image) => {
              return new Promise((resolve) => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");

                const img = new Image();
                img.onload = () => {
                  let width = img.width;
                  let height = img.height;

                  if (width > height) {
                    if (width > maxSize) {
                      height *= maxSize / width;
                      width = maxSize;
                    }
                  } else {
                    if (height > maxSize) {
                      width *= maxSize / height;
                      height = maxSize;
                    }
                  }

                  canvas.width = width;
                  canvas.height = height;

                  ctx.drawImage(img, 0, 0, width, height);

                  canvas.toBlob(
                    (blob) => {
                      resolve(blob);
                    },
                    "image/jpeg",
                    0.9,
                  ); // You can change the format and quality here
                };

                img.src = URL.createObjectURL(image);
              });
            };

            const resizedBlob = await resizeImage(file);

            // Upload the resized file to S3
            const newThumbnailImgPut = await Storage.put(
              "ProductBundles/" +
                this.editedItem.id +
                "/Thumbnails/" +
                Date.now() +
                "-" +
                file.name,
              resizedBlob,
            ).catch((err) => console.log(err));

            if (!this.editedItem.thumbnails) {
              this.editedItem.thumbnails = [];
            }

            if (!this.editedItem.thumbnails.includes(newThumbnailImgPut.key)) {
              const mutableThumbnails = _.cloneDeep(this.editedItem.thumbnails);

              mutableThumbnails.push(newThumbnailImgPut.key);
              this.editedItem.thumbnails = mutableThumbnails;

              // Fetch the image source for the newly added thumbnail
              const thumbnailImgSrc = await this.getThumbnailImage(
                newThumbnailImgPut.key,
              );

              // Update the thumbnailImagesSrc array with the new source
              this.thumbnailImagesSrc.push({
                key: newThumbnailImgPut.key,
                src: thumbnailImgSrc,
              });

              this.editedItem.searchField = JSON.stringify(
                _.values(
                  _.omit(
                    this.editedItem,
                    this.productBundleSearchExcludeFields,
                  ),
                ),
              ).toLowerCase();

              await DataStore.save(
                ProductBundle.copyOf(
                  _.find(this.unfilteredProductBundlesArray, {
                    id: this.editedItem.id,
                  }),
                  (productBundle) => {
                    productBundle.thumbnails = this.editedItem.thumbnails;
                    productBundle.searchField = this.editedItem.searchField;
                  },
                ),
              )
                .catch((err) => {
                  console.log(err);
                })
                .then(() => {
                  this.unEditedProductBundle.thumbnails = _.cloneDeep(
                    this.editedItem.thumbnails,
                  );
                  this.unEditedProductBundle.searchField =
                    this.editedItem.searchField;
                });
            }

            this.loading = false;
          }
        });

        document.body.appendChild(inputElement);
        inputElement.click();
      } catch (error) {
        console.log(error);
        this.loading = false;
      }
    },

    resetForm() {
      if (this.$refs.form) this.$refs.form.reset();
      this.editedItem = _.cloneDeep(this.defaultItem);

      this.editedRecommendations = [];
      this.unEditedRecommendations = [];

      this.valid = false;
    },

    createItem() {
      this.resetForm();

      this.editedItem.searchField = JSON.stringify(
        _.values(
          _.omit(this.editedItem, this.productBundleSearchExcludeFields),
        ),
      ).toLowerCase();

      this.editedItem.publishDate = new Date().toISOString();

      this.productBundlePublishDate = this.editedItem.publishDate.substring(
        0,
        10,
      );

      this.productBundlePublishTime = new Date(
        new Date(this.editedItem.publishDate).getTime() + 2 * 60 * 60 * 1000,
      )
        .toISOString()
        .substring(11, 16);

      this.dialog = true;
    },

    async duplicateItem(item) {
      try {
        this.loading = true;

        const dupEditedItem = _.cloneDeep(item);

        dupEditedItem.thumbnails = [];

        dupEditedItem.recommendations = item.recommendations
          ? _.cloneDeep(item.recommendations)
          : null;

        dupEditedItem.searchField = JSON.stringify(
          _.values(
            _.omit(dupEditedItem, this.productBundleSearchExcludeFields),
          ),
        ).toLowerCase();

        const newProductBundle = await DataStore.save(
          new ProductBundle({
            title: dupEditedItem.title.trim() + " - COPY",
            description: dupEditedItem.description
              ? dupEditedItem.description.trim()
              : "",
            price:
              typeof dupEditedItem.price === "string"
                ? parseFloat(dupEditedItem.price)
                : dupEditedItem.price,
            discountPercentage:
              typeof dupEditedItem.discountPercentage === "string"
                ? parseFloat(dupEditedItem.discountPercentage)
                : dupEditedItem.discountPercentage,
            thumbnails: [],
            tags: dupEditedItem.tags?.length > 0 ? dupEditedItem.tags : [],
            products:
              dupEditedItem.products?.length > 0 ? dupEditedItem.products : [],
            publishDate: dupEditedItem.publishDate,
            featured: dupEditedItem.featured,
            searchField: dupEditedItem.searchField,
            categoryId: dupEditedItem.categoryId,
            subjectID: dupEditedItem.subjectID || null,
            videoPreview:
              dupEditedItem.videoPreview > ""
                ? dupEditedItem.videoPreview
                : null,
            recommendations: dupEditedItem.recommendations,
          }),
        );
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    async editItem(item) {
      this.editedIndex = this.unfilteredProductBundlesArray.findIndex(
        (obj) => obj.id === item.id,
      );

      this.editedItem = _.cloneDeep(item);

      if (item.thumbnails) {
        item.thumbnails.forEach(async (thumbnailKey) => {
          if (!_.find(this.thumbnailImagesSrc, { key: thumbnailKey })) {
            const thumbnailImgSrc = await this.getThumbnailImage(thumbnailKey);
            this.thumbnailImagesSrc.push({
              key: thumbnailKey,
              src: thumbnailImgSrc,
            });
          }
        });
      }
      if (item.publishDate) {
        this.productBundlePublishDate = item.publishDate.substring(0, 10);
        this.productBundlePublishTime = new Date(
          new Date(item.publishDate).getTime() + 2 * 60 * 60 * 1000,
        )
          .toISOString()
          .substring(11, 16);
      }

      this.editedRecommendations = _.cloneDeep(item.recommendations || []);
      this.unEditedRecommendations = _.cloneDeep(this.editedRecommendations);

      this.unEditedProductBundle = _.cloneDeep(this.editedItem);
      this.dialog = true;
    },

    deleteItem(item) {
      const itemToDelete = this.unfilteredProductBundlesArray.find(
        (productBundle) => productBundle.id === item.id,
      );
      if (!itemToDelete) return;
      this.editedIndex = this.unfilteredProductBundlesArray.findIndex(
        (obj) => obj.id === item.id,
      );
      this.editedItem = _.cloneDeep(item);
      this.dialogDelete = true;
    },

    async deleteItemConfirm() {
      this.deletingItem = true;
      try {
        // this.productBundles.splice(this.editedIndex, 1);
        await DataStore.delete(
          this.unfilteredProductBundlesArray[this.editedIndex],
        );

        for (const thumbnailKey of this.editedItem.thumbnails) {
          await Storage.remove(thumbnailKey, {
            level: "public",
          });
        }
      } catch (error) {
        console.log(error);
      }

      this.deletingItem = false;
      this.closeDelete();
    },

    async toggleSoftDeleteProductBundle(bundle) {
      try {
        const confirmAction = bundle.softDeleted
          ? "Are you sure you want to restore this bundle? It will become visible to users."
          : "Are you sure you want to soft delete this bundle? It will be hidden from all users.";

        if (!confirm(confirmAction)) {
          return;
        }

        this.loading = true;

        // Get the source object from unfilteredProductBundlesArray (ensures valid model)
        const sourceBundle = _.find(this.unfilteredProductBundlesArray, {
          id: bundle.id,
        });

        if (!sourceBundle) {
          console.error("Bundle not found in unfilteredProductBundlesArray");
          this.loading = false;
          return;
        }

        // Soft delete/restore the bundle
        const updatedBundle = await DataStore.save(
          ProductBundle.copyOf(sourceBundle, (updated) => {
            updated.softDeleted = !bundle.softDeleted;
          }),
        );

        // Update the local productBundles array
        const index = this.productBundles.findIndex(
          (b) => b.id === updatedBundle.id,
        );
        if (index !== -1) {
          this.$set(this.productBundles, index, updatedBundle);
        }

        console.log(
          bundle.softDeleted
            ? "Bundle restored successfully"
            : "Bundle soft deleted successfully",
        );

        // Refresh the query to update the table immediately
        await this.changeProductBundlesSearchQuery();
      } catch (error) {
        console.error("Error toggling soft delete:", error);
      } finally {
        this.loading = false;
      }
    },

    async cancel() {
      this.cancelingChanges = true;

      try {
        this.thumbnailImagesSrc.forEach(async (thumbnailImgSrc) => {
          const thumbnailKey = thumbnailImgSrc.key;

          if (!this.unEditedProductBundle.thumbnails.includes(thumbnailKey)) {
            _.remove(this.thumbnailImagesSrc, { key: thumbnailKey });

            await Storage.remove(thumbnailKey, {
              level: "public",
            });
          }
        });

        if (this.unEditedProductBundle.thumbnails) {
          this.unEditedProductBundle.thumbnails.forEach(
            async (thumbnailKey) => {
              if (!_.find(this.thumbnailImagesSrc, { key: thumbnailKey })) {
                const thumbnailImgSrc = await this.getThumbnailImage(
                  thumbnailKey,
                );
                this.thumbnailImagesSrc.push({
                  key: thumbnailKey,
                  src: thumbnailImgSrc,
                });
              }
            },
          );
        }

        this.editedItem = _.cloneDeep(this.unEditedProductBundle);

        this.editedRecommendations = _.cloneDeep(this.unEditedRecommendations);

        this.editedItem.searchField = JSON.stringify(
          _.values(
            _.omit(this.editedItem, this.productBundleSearchExcludeFields),
          ),
        ).toLowerCase();

        await DataStore.save(
          ProductBundle.copyOf(
            _.find(this.unfilteredProductBundlesArray, {
              id: this.editedItem.id,
            }),
            (updateModel) => {
              (updateModel.title = this.editedItem.title.trim()),
                (updateModel.description = this.editedItem.description
                  ? this.editedItem.description.trim()
                  : ""),
                (updateModel.price =
                  typeof this.editedItem.price === "string"
                    ? parseFloat(this.editedItem.price)
                    : this.editedItem.price),
                (updateModel.discountPercentage =
                  typeof this.editedItem.discountPercentage === "string"
                    ? parseFloat(this.editedItem.discountPercentage)
                    : this.editedItem.discountPercentage),
                (updateModel.thumbnails = this.editedItem.thumbnails),
                (updateModel.tags =
                  this.editedItem.tags?.length > 0 ? this.editedItem.tags : []),
                (updateModel.products =
                  this.editedItem.products?.length > 0
                    ? this.editedItem.products
                    : []),
                (updateModel.publishDate = this.editedItem.publishDate),
                (updateModel.featured = this.editedItem.featured),
                (updateModel.searchField = this.editedItem.searchField),
                (updateModel.categoryId = this.editedItem.categoryId),
                (updateModel.subjectID = this.editedItem.subjectID || null),
                (updateModel.videoPreview =
                  this.editedItem.videoPreview > ""
                    ? this.editedItem.videoPreview
                    : null),
                (updateModel.recommendations =
                  this.editedRecommendations.length === 0
                    ? null
                    : this.editedRecommendations);
            },
          ),
        );
      } catch (error) {
        console.log(error);
      }

      this.$nextTick(() => {
        this.dialogCancel = false;
        this.cancelingChanges = false;
      });
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = _.cloneDeep(this.defaultItem);
        this.editedIndex = -1;
        this.thumbnailImagesSrc = _.cloneDeep([]);
        this.productBundlePublishDate = "";
        this.productBundlePublishTime = "";
        this.unEditedProductBundle = {};

        this.editedRecommendations = [];
        this.unEditedRecommendations = [];
      });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = _.cloneDeep(this.defaultItem);
        this.editedIndex = -1;
        this.thumbnailImagesSrc = _.cloneDeep([]);
        this.productBundlePublishDate = "";
        this.productBundlePublishTime = "";
      });
    },

    closeCancel() {
      this.dialogCancel = false;
    },

    async save() {
      this.loading = true;
      if (this.editedIndex > -1) {
        // Update schedule
        await this.updateProductBundle();
      } else {
        // Create new schedule
        await this.createProductBundle();
      }

      this.unEditedProductBundle = _.cloneDeep(this.editedItem);
      this.unEditedRecommendations = _.cloneDeep(this.editedRecommendations);

      this.$nextTick(() => {
        this.loading = false;
      });
    },

    async createProductBundle() {
      try {
        this.editedItem.searchField = JSON.stringify(
          _.values(
            _.omit(this.editedItem, this.productBundleSearchExcludeFields),
          ),
        ).toLowerCase();

        const newProductBundle = await DataStore.save(
          new ProductBundle({
            title: this.editedItem.title.trim(),
            description: this.editedItem.description
              ? this.editedItem.description.trim()
              : "",
            price:
              typeof this.editedItem.price === "string"
                ? parseFloat(this.editedItem.price)
                : this.editedItem.price,
            discountPercentage:
              typeof this.editedItem.discountPercentage === "string"
                ? parseFloat(this.editedItem.discountPercentage)
                : this.editedItem.discountPercentage,
            thumbnails: [],
            tags: this.editedItem.tags?.length > 0 ? this.editedItem.tags : [],
            products:
              this.editedItem.products?.length > 0
                ? this.editedItem.products
                : [],
            publishDate: this.editedItem.publishDate,
            featured: this.editedItem.featured,
            searchField: this.editedItem.searchField,
            categoryId: this.editedItem.categoryId,
            subjectID: this.editedItem.subjectID || null,
            videoPreview:
              this.editedItem.videoPreview > ""
                ? this.editedItem.videoPreview
                : null,
            recommendations:
              this.editedRecommendations.length > 0
                ? this.editedRecommendations
                : null,
          }),
        );

        this.editedRecommendations = _.cloneDeep(
          newProductBundle.recommendations || [],
        );

        for (const rec of this.editedRecommendations) {
          await this.updateReciprocalRecommendation(newProductBundle.id, rec);
        }

        const productCategory =
          this.syncedProductCategoryArray.find(
            (cat) => cat.id === this.editedItem.categoryId,
          ) || null;

        // Generate seoId
        const seoId = `product/${this.generateSeoId(
          this.editedItem.title,
          productCategory,
        )}`;
        if (!seoId) {
          console.warn(
            "Failed to generate seoId for bundle:",
            newProductBundle.id,
          );
        } else {
          // Prepare ProductSEO properties
          const redirectProps = {
            seoId,
            ProductBundle: newProductBundle,
          };

          await DataStore.save(new ProductSEO(redirectProps)).catch((err) => {
            console.error("Error creating ProductSEO:", err);
          });
        }

        this.editedItem.id = newProductBundle.id;
        this.editedIndex = 0;
      } catch (error) {
        console.log(error);
      }
    },

    async updateProductBundle() {
      try {
        this.editedItem.searchField = JSON.stringify(
          _.values(
            _.omit(this.editedItem, this.productBundleSearchExcludeFields),
          ),
        ).toLowerCase();

        const updatedProductBundle = await DataStore.save(
          ProductBundle.copyOf(
            _.find(this.unfilteredProductBundlesArray, {
              id: this.editedItem.id,
            }),
            (updateModel) => {
              (updateModel.title = this.editedItem.title.trim()),
                (updateModel.description = this.editedItem.description
                  ? this.editedItem.description.trim()
                  : ""),
                (updateModel.price =
                  typeof this.editedItem.price === "string"
                    ? parseFloat(this.editedItem.price)
                    : this.editedItem.price),
                (updateModel.discountPercentage =
                  typeof this.editedItem.discountPercentage === "string"
                    ? parseFloat(this.editedItem.discountPercentage)
                    : this.editedItem.discountPercentage),
                (updateModel.thumbnails = this.editedItem.thumbnails),
                (updateModel.tags =
                  this.editedItem.tags?.length > 0 ? this.editedItem.tags : []),
                (updateModel.products =
                  this.editedItem.products?.length > 0
                    ? this.editedItem.products
                    : []),
                (updateModel.publishDate = this.editedItem.publishDate),
                (updateModel.featured = this.editedItem.featured),
                (updateModel.searchField = this.editedItem.searchField),
                (updateModel.categoryId = this.editedItem.categoryId),
                (updateModel.subjectID = this.editedItem.subjectID || null),
                (updateModel.videoPreview =
                  this.editedItem.videoPreview > ""
                    ? this.editedItem.videoPreview
                    : null),
                (updateModel.recommendations =
                  this.editedRecommendations.length > 0
                    ? this.editedRecommendations
                    : null);
            },
          ),
        );

        this.editedRecommendations = _.cloneDeep(
          updatedProductBundle.recommendations || [],
        );

        const addedRecommendations = this.editedRecommendations.filter(
          (rec) =>
            !this.unEditedRecommendations.some(
              (u) => u.productId === rec.productId,
            ),
        );
        for (const rec of addedRecommendations) {
          await this.updateReciprocalRecommendation(
            updatedProductBundle.id,
            rec,
          );
        }

        const productCategory =
          this.syncedProductCategoryArray.find(
            (cat) => cat.id === this.editedItem.categoryId,
          ) || null;

        // Handle ProductSEO
        const seoId = `product/${this.generateSeoId(
          this.editedItem.title,
          productCategory,
        )}`;
        if (!seoId) {
          console.warn(
            "Failed to generate seoId for bundle:",
            this.editedItem.id,
          );
        } else {
          // If the title changed, we leave the old ProductSEO untouched and
          // create a brand new one with the new seoId (so the old link still resolves).
          const titleChanged =
            (this.unEditedProductBundle.title || "").trim() !==
            (this.editedItem.title || "").trim();

          const existingProductSEO =
            this.syncedProductSEOArray.find(
              (seo) => seo.productBundleID === this.editedItem.id,
            ) || null;

          console.log("existingProductSEO", existingProductSEO);
          console.log("titleChanged", titleChanged);

          const redirectProps = {
            seoId,
            ProductBundle: updatedProductBundle,
          };

          console.log("redirectProps", redirectProps);

          if (existingProductSEO && !titleChanged) {
            // Title unchanged -> keep the existing ProductSEO in sync.
            await DataStore.save(
              ProductSEO.copyOf(existingProductSEO, (updated) => {
                updated.seoId = seoId;
                updated.ProductBundle = redirectProps.ProductBundle;
              }),
            ).catch((err) => {
              console.error("Error updating ProductSEO:", err);
            });
          } else {
            // Title changed (or no SEO existed yet) -> create a NEW ProductSEO
            // and leave any existing/older ProductSEO records untouched.
            await DataStore.save(new ProductSEO(redirectProps)).catch((err) => {
              console.error("Error creating ProductSEO:", err);
            });
          }
        }
      } catch (error) {
        console.log(error);
      }
    },

    bundleProductsTotal(bundle) {
      if (!bundle.products || bundle.products.length === 0) {
        return 0;
      }

      // Same price calculation as totalProductsPrice
      return bundle.products.reduce((sum, productObj) => {
        const productId =
          typeof productObj === "string" ? productObj : productObj?.id;
        const product = this.syncedProductArray.find((p) => p.id === productId);
        if (product) {
          let price = product.price || 0;
          if (product.discountPercentage) {
            price -= (price * product.discountPercentage) / 100;
          }
          return sum + price;
        }
        return sum;
      }, 0);
    },

    bundlePriceMismatch(bundle) {
      if (!bundle.products || bundle.products.length === 0) {
        return false;
      }

      return (
        (bundle.price || 0).toFixed(2) !==
        this.bundleProductsTotal(bundle).toFixed(2)
      );
    },

    async resetProductBundlePrices() {
      this.bulkEditLoading = "reset_price";

      try {
        // Mismatched bundles currently shown in the data table,
        // resolved to their DataStore model instances
        const mismatchedBundles = this.unfilteredProductBundlesArray.filter(
          (bundle) =>
            this.productBundles.some((p) => p.id === bundle.id) &&
            this.bundlePriceMismatch(bundle),
        );

        for (const bundle of mismatchedBundles) {
          const newPrice =
            Math.round(this.bundleProductsTotal(bundle) * 100) / 100;

          await DataStore.save(
            ProductBundle.copyOf(bundle, (updated) => {
              updated.price = newPrice;
              updated.searchField = JSON.stringify(
                _.values(
                  _.omit(updated, this.productBundleSearchExcludeFields),
                ),
              ).toLowerCase();
            }),
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.bulkEditLoading = null;
      }
    },

    async queryProductBundles() {
      try {
        const search = this.productBundlesDataTableSearch.split(" ");
        this.selectedProductBundles = [];

        const productBundleCount = (
          await DataStore.query(ProductBundle, (productBundle) =>
            productBundle.and((f) => [
              this.productBundlesDataTableSearchTags.every((tag) =>
                f.tags("contains", tag),
              ),
              f.or((f) =>
                this.productBundlesDataTableSearchCategories.reduce(
                  (c, id) => c.categoryId("eq", id),
                  f,
                ),
              ),
              this.productBundlesDataTableSearchSubjects.length > 0
                ? f.or((o) =>
                    this.productBundlesDataTableSearchSubjects.map((subjectID) =>
                      subjectID === NO_SUBJECT
                        ? o.subjectID("eq", null)
                        : o.subjectID("eq", subjectID),
                    ),
                  )
                : f,
              search
                .map((str) => str.toLowerCase())
                .every((string) => f.searchField("contains", string)),
              this.showOnlySoftDeleted
                ? f.softDeleted("eq", true)
                : f.softDeleted("ne", true),
            ]),
          )
        ).length;

        this.productBundlesDataTableServerItemsLength = productBundleCount || 0;

        const filterObj = {
          page: this.productBundlesDataTablePageCount - 1,
          limit: this.productBundlesDataTableRowsPerPage,
          sort: (s) =>
            s[this.sortBy](
              this.sortDesc
                ? SortDirection.DESCENDING
                : SortDirection.ASCENDING,
            ),
        };

        if (
          this.productBundlesDataTableRowsPerPage === -1 ||
          this.showPriceMismatches
        ) {
          // Price mismatches can't be filtered in the DataStore query,
          // so fetch all matching bundles and paginate client-side
          delete filterObj.page;
          delete filterObj.limit;
        }

        let queriedProductBundles = await DataStore.query(
          ProductBundle,
          (productBundle) =>
            productBundle.and((f) => [
              this.productBundlesDataTableSearchTags.every((tag) =>
                f.tags("contains", tag),
              ),
              f.or((f) =>
                this.productBundlesDataTableSearchCategories.reduce(
                  (c, id) => c.categoryId("eq", id),
                  f,
                ),
              ),
              this.productBundlesDataTableSearchSubjects.length > 0
                ? f.or((o) =>
                    this.productBundlesDataTableSearchSubjects.map((subjectID) =>
                      subjectID === NO_SUBJECT
                        ? o.subjectID("eq", null)
                        : o.subjectID("eq", subjectID),
                    ),
                  )
                : f,
              search
                .map((str) => str.toLowerCase())
                .every((string) => f.searchField("contains", string)),
              this.showOnlySoftDeleted
                ? f.softDeleted("eq", true)
                : f.softDeleted("ne", true),
            ]),
          filterObj,
        );

        if (this.showPriceMismatches) {
          queriedProductBundles = queriedProductBundles.filter((bundle) =>
            this.bundlePriceMismatch(bundle),
          );

          this.productBundlesDataTableServerItemsLength =
            queriedProductBundles.length;

          if (this.productBundlesDataTableRowsPerPage !== -1) {
            let start =
              (this.productBundlesDataTablePageCount - 1) *
              this.productBundlesDataTableRowsPerPage;

            // Reset to first page if the current page is out of range
            if (start >= queriedProductBundles.length) {
              this.productBundlesDataTablePageCount = 1;
              start = 0;
            }

            queriedProductBundles = queriedProductBundles.slice(
              start,
              start + this.productBundlesDataTableRowsPerPage,
            );
          }
        }

        this.syncedProductBundleArray = queriedProductBundles;

        // this.productBundles = _.cloneDeep(productBundles);

        // Loop through queriedProductBundles to update or add new productBundles
        queriedProductBundles.forEach((newProductBundle) => {
          // console.log("newProductBundle:", newProductBundle);
          const existingProductBundleIndex = this.productBundles.findIndex(
            (productBundle) => productBundle.id === newProductBundle.id,
          );
          // console.log("existingProductBundleIndex:", existingProductBundleIndex);

          if (existingProductBundleIndex !== -1) {
            const existingProductBundle =
              this.productBundles[existingProductBundleIndex];

            if (
              existingProductBundle.updatedAt !== newProductBundle.updatedAt
            ) {
              // Update the productBundle while preserving thumbnailImages
              const updatedProductBundle = {
                ...newProductBundle,
                thumbnailImages: existingProductBundle.thumbnailImages,
              };
              this.productBundles.splice(
                existingProductBundleIndex,
                1,
                updatedProductBundle,
              );
            }
          } else {
            // If new productBundle doesn't exist in this.productBundles, add it
            this.productBundles.push({
              ...newProductBundle,
              thumbnailImages: [],
            });
          }
        });

        // // Remove productBundles not in sortedProductBundles
        this.productBundles = this.productBundles.filter((productBundle) =>
          queriedProductBundles.some(
            (newProductBundle) => newProductBundle.id === productBundle.id,
          ),
        );

        this.productBundles = _.sortBy(this.productBundles, [
          (p) => p.featured || false,
          (p) => new Date(p.publishDate),
          (p) => p.title,
        ]).reverse();
      } catch (error) {
        console.log(error);
      }
    },

    syncData() {
      console.log("Syncing data...ADMINPRODUCTBUNDLES");

      try {
        this.syncProducts();
        this.syncProductBundles();

        const productSEOSubscription = DataStore.observeQuery(
          ProductSEO,
        ).subscribe(
          async (snapshot) => {
            const { isSynced, items } = snapshot;
            if (isSynced && !_.isEqual(items, this.syncedProductSEOArray)) {
              this.syncedProductSEOArray = items;
            }
          },
          (error) => {
            console.log(error);
          },
        );

        const tagSubscription = DataStore.observeQuery(ProductTag).subscribe(
          async (snapshot) => {
            const { isSynced, items } = snapshot;
            if (
              // isSynced &&
              !_.isEqual(items, this.syncedProductTagArray)
            ) {
              this.syncedProductTagArray = items;
              this.productTagNameArray = items.map((tag) => {
                return tag.name;
              });
            }
          },
          (error) => {
            console.log(error);
          },
        );

        const categorySubscription = DataStore.observeQuery(
          ProductCategory,
        ).subscribe(
          async (snapshot) => {
            const { isSynced, items } = snapshot;
            if (
              // isSynced &&
              !_.isEqual(items, this.syncedProductCategoryArray)
            ) {
              this.syncedProductCategoryArray = items;
              // this.productCategoryNameArray = items.map((category) => {
              //   return category.name;
              // });
            }
          },
          (error) => {
            console.log(error);
          },
        );

        store.commit("addSubscriptions", [
          { model: "ProductSEO", sub: productSEOSubscription },
          { model: "ProductTag", sub: tagSubscription },
          { model: "ProductCategory", sub: categorySubscription },
        ]);
      } catch (error) {
        console.log(error);
      }
    },

    syncProducts() {
      const productSubscription = DataStore.observeQuery(Product).subscribe(
        async (snapshot) => {
          const { isSynced, items } = snapshot;
          if (
            // isSynced &&
            !_.isEqual(items, this.syncedProductArray)
          ) {
            this.syncedProductArray = items;
            this.nonQuestionProductArray = items
              .filter(
                (product) =>
                  !product.questions || product.questions?.length === 0,
              )
              .map((product) => ({ id: product.id, title: product.title }));
          }

          const productBundleParams = this.productBundleParams;

          if (isSynced) {
            this.syncedProducts = true;

            if (productBundleParams) {
              this.editedItem = {
                ...this.editedItem,
                ...productBundleParams,
              };

              if (productBundleParams.products) {
                // console.log("params.products", productBundleParams.products);

                const nonQuestionProducts = this.syncedProductArray
                  .filter(
                    (product) =>
                      productBundleParams.products.includes(product.id) &&
                      (!product.questions || product.questions?.length === 0),
                  )
                  .map((product) => ({
                    id: product.id,
                    title: product.title,
                  }));

                // console.log("nonQuestionProductIDs", nonQuestionProductIDs);
                this.editedItem.products = nonQuestionProducts;
              }

              store.commit("changeProductBundleParams", null);
            }
          } else if (!isSynced) {
            this.syncedProducts = false;
          }
        },
        (error) => {
          console.log(error);
        },
      );

      store.commit("addSubscription", {
        model: "Product",
        sub: productSubscription,
      });
    },

    syncProductBundles() {
      const productBundleSubscription = DataStore.observeQuery(
        ProductBundle,
      ).subscribe(
        async (snapshot) => {
          const { isSynced, items } = snapshot;

          this.unfilteredProductBundlesArray = items;

          if (items?.length > 0 && !this.changingDatastoreConfig) {
            console.log("Synced productBundles queryProductbundles");
            await this.queryProductBundles();
          }

          if (isSynced) {
            this.syncedProductBundles = true;
          } else {
            this.syncedProductBundles = false;
          }
        },
        (error) => {
          console.log(error);
        },
      );

      store.commit("addSubscription", {
        model: "ProductBundle",
        sub: productBundleSubscription,
      });
    },
  },

  beforeDestroy() {
    store.commit("removeSubscriptions", [
      "Product",
      "ProductSEO",
      "ProductBundle",
      "ProductTag",
      "ProductCategory",
      "ProductContent",
    ]);

    store.commit("removeDatastorePredicates", [
      "Product",
      "ProductSEO",
      "ProductBundle",
      "ProductTag",
      "ProductCategory",
      "ProductContent",
    ]);
  },
};
</script>

<style lang="scss">
.productBundlesSearchInputCSS .v-input__slot {
  margin-bottom: 0px !important;
}

.productBundlesSearchInputCSS .v-select__selections {
  min-height: 36px !important;
}

.thumbnailvrow {
  margin-left: -12px;
  margin-right: -12px;
}

/* #productBundlesDataTable .v-data-footer__pagination {
  visibility: hidden;
}
#productBundlesDataTable .v-data-footer__select {
  visibility: hidden;
} */

.t-datetime-picker {
  .v-tabs-slider-wrapper {
    top: 0;
  }
  .v-picker__title {
    height: 90px;
  }
  .v-time-picker-title__time * {
    font-size: 60px;
    height: 60px;
  }
  .v-picker__body {
    height: 290px;
  }
  .v-tabs-items {
    height: 380px;
  }
}

#expPanelContent > * {
  padding: 12px !important;
}

.plannerPanelTopBorder > div:nth-child(2) {
  border-top-right-radius: 4px !important;
  border-top-left-radius: 4px !important;
}

.plannerPanelTopBorder .v-expansion-panel:first-child {
  border-top-right-radius: 4px !important;
  border-top-left-radius: 4px !important;
}

.plannerPanelTopBorder .v-expansion-panel--active + div {
  border-top-right-radius: 4px !important;
  border-top-left-radius: 4px !important;
}

.plannerPanelTopBorder .v-expansion-panel:last-child {
  border-bottom-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
}

.plannerPanelTopBorder .v-expansion-panel--next-active {
  border-bottom-right-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
}

.plannerPanelTopBorder .v-expansion-panel--active {
  border-radius: 4px !important;
}

.plannerPanelTopBorder .v-expansion-panel--active #expPanelContent {
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
}
</style>
